import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Shuffle, Snowflake } from "lucide-react";
import { getItemAssetKey } from "@craftoff/asset-pack-core";
import {
  availableQuantity,
  cosmeticItem,
  DEFAULT_COSMETIC_LOADOUT,
  ingredientCounts,
  hashSeed,
  isRankedUnlocked,
  MATCH_START_SEQUENCE_MS,
  normalizeRecipeGrid,
  normalizeCosmeticLoadout,
  playerLevelProgress,
  RANKED_UNLOCK_XP,
  recipeShapeHint,
  shuffleSeeded,
  ROUND_START_SEQUENCE_MS,
  ROUNDS_TO_WIN,
  SPECIAL_ITEM_CATALOG,
  selectRivalXp,
  type Grid,
  type CosmeticLoadout,
  type InventorySlot,
  type ItemId,
  type MatchMode,
  type Recipe
} from "@craftoff/game-core";
import { MATCH_FORFEIT_CLOSE_CODE } from "@craftoff/protocol/constants";
import {
  MATCH_BINARY_SUBPROTOCOL,
  decodeBinaryMatchMessage,
  encodeForfeit,
  encodePlaceItem,
  encodeRemoveItem,
  encodeStateRequest,
  type CoinRewardResult,
  type MasterCraftResult
} from "@craftoff/protocol";
import { ITEMS, MATCH_RECIPES, RECIPES } from "@craftoff/recipe-data";
import { playAssetSound, playTone } from "./audio";
import {
  claimAccountProfile,
  equipSpecialItem,
  loadAccountSession,
  loadCanonicalProgress,
  loadShop,
  pendingAccountClaim,
  signOutAccount,
  useSpecialItem,
  type AccountSession,
  type MatchmakingResult,
  type SpecialItemCounts
} from "./account-api";
import { AccountDialog, LeaderboardScreen, ProfileStatsModal, ShopScreen, type ShopCategory } from "./AccountViews";
import {
  ACCOUNT_PRESENTATION_KEY,
  clearAccountPresentation,
  loadInitialAccount,
  saveAccountPresentation
} from "./account-presentation";
import { AboutScreen } from "./AboutView";
import { ADS_ENABLED, ADSENSE_CLIENT, loadAdSense } from "./ad-config";
import { AvatarFaceplate, cosmeticTableStyle, cosmeticThemeClass } from "./Cosmetics";
import { battleIntroPhase } from "./battle-intro";
import { clientError, clientLog, clientWarn } from "./client-log";
import { useGameStore, type AiDifficulty, type OnlineAction } from "./game-store";
import { LegalLinks, PrivacyPolicyScreen, TermsOfServiceScreen } from "./LegalViews";
import { OnlineActionTracker } from "./online-action-tracker";
import { roundTransitionState } from "./round-transition";
import { useAssets } from "./use-assets";
import { useOnlinePlayers } from "./use-online-players";
import { useMatchmaking, type MatchOffer, type MatchmakingQueueState } from "./use-matchmaking";
import { apiUrl, haptic, isNativeBuild, nativeApiOrigin, webSocketUrl } from "./platform";

type AppScreen = "menu" | "queue" | "battle" | "learn" | "leaderboard" | "shop" | "about" | "privacy" | "terms";
type AccountAction = "ranked" | "leaderboard" | "shop";
type QueueState = MatchmakingQueueState;
type MatchedResult = Extract<MatchmakingResult, { status: "matched" }>;
const DOUBLE_ACTIVATION_WINDOW_MS = 450;
const CRAFTING_BLUEPRINT_ID = "crafting-blueprint";
const FREEZE_SPECIAL_ITEM_ID = "freeze";
const SCRAMBLE_SPECIAL_ITEM_ID = "scramble";
const SPECIAL_FREEZE_DURATION_MS = 5_000;
const DIRECT_GAMEPLAY_ORIGIN = typeof __CRAFTOFF_GAMEPLAY_ORIGIN__ === "string"
  ? __CRAFTOFF_GAMEPLAY_ORIGIN__
  : "";

function newBattleAbilityID(preferred?: string): string {
  if (preferred && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(preferred)) return preferred;
  return crypto.randomUUID();
}

function canLoadAdSense(): boolean {
  return !isNativeBuild && (window.location.hostname === "craftoff.io" || window.location.hostname === "www.craftoff.io");
}

type MatchStateMessage = {
  matchId: string;
  round: number;
  recipeId: string | null;
  completedRecipeId: string | null;
  intermissionWinnerId: string | null;
  intermissionReason: "completed" | "timeout" | null;
  roundStartsAt: number | null;
  deadline: number | null;
  intermissionEndsAt: number | null;
  score: [number, number];
  status: "waiting" | "countdown" | "active" | "intermission" | "complete" | "aborted";
  youIndex: number;
  lastSequence: number;
  revision: number;
  inventory: InventorySlot[];
  players: Array<{ id: string; grid: Grid }>;
};

type MatchSocketMessage = {
  type?: string;
  state?: MatchStateMessage | { grid: Grid; inventory: InventorySlot[] };
  round?: number;
  recipeId?: string;
  inventory?: InventorySlot[];
  startsAt?: number;
  deadline?: number;
  playerId?: string;
  grid?: Grid;
  winnerId?: string | null;
  score?: [number, number];
  sequence?: number;
  reason?: string;
  code?: string;
  intermissionEndsAt?: number;
  pendingProgression?: boolean;
  xp?: number;
  rankedRating?: number;
  wins?: number;
  losses?: number;
  progressionVersion?: number;
  coinBalance?: number;
  coinReward?: CoinRewardResult;
  masterCraft?: MasterCraftResult | null;
  masterCrafts?: MasterCraftResult[];
};

function itemName(itemId: ItemId): string {
  return ITEMS[itemId]?.name ?? itemId.split("_").map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`).join(" ");
}

function ItemIcon({ itemId, resolve, large = false }: { itemId: ItemId; resolve: (id: string) => string; large?: boolean }) {
  const src = resolve(getItemAssetKey(itemId));
  if (!src) return <span className={large ? "item-placeholder target-item" : "item-placeholder"} />;
  return <img className={large ? "item-icon target-item" : "item-icon"} src={src} alt={itemName(itemId)} draggable={false} />;
}

function MysteryBlock() {
  return <span className="concealed-item mystery-block" aria-hidden="true"><b>?</b></span>;
}

function Logo() {
  return <div className="craftoff-logo" aria-label="CRAFTOFF"><img src="/brand/craftoff-wordmark.svg" alt="" aria-hidden="true" draggable={false} /></div>;
}

function HomepageLogo() {
  return <h1 className="craftoff-logo" aria-label="CraftOff"><img src="/brand/craftoff-wordmark.svg" alt="" aria-hidden="true" draggable={false} /></h1>;
}

function MatchModeBadge({ mode }: { mode: MatchMode }) {
  const label = mode === "ranked" ? "RANKED MATCH" : "QUICK MATCH";
  const compactLabel = mode === "ranked" ? "RANKED" : "QUICK";
  return (
    <span className={`match-mode-badge ${mode}`} aria-label={label}>
      <span className="match-mode-label-full">{label}</span>
      <span className="match-mode-label-compact" aria-hidden="true">{compactLabel}</span>
    </span>
  );
}

function MinecraftButton({ children, onClick, subtle = false, disabled = false }: { children: React.ReactNode; onClick: () => void; subtle?: boolean; disabled?: boolean }) {
  return <button className={`minecraft-button ${subtle ? "subtle" : ""}`} onClick={onClick} disabled={disabled}>{children}</button>;
}

function ExperienceBar({ xp }: { xp: number }) {
  const level = playerLevelProgress(xp);
  return (
    <div className="experience-card" aria-label={`${level.xp} experience points. Level ${level.level}. ${level.xpRemaining} experience points to level ${level.level + 1}.`}>
      <div className="experience-copy">
        <span>LEVEL {level.level}</span>
        <strong>{level.xp.toLocaleString()} XP</strong>
      </div>
      <div className="experience-track"><i style={{ width: `${level.progress * 100}%` }} /></div>
      <small>{level.xpRemaining.toLocaleString()} XP TO LEVEL {level.level + 1}</small>
    </div>
  );
}

function OnlinePlayersCard({ count }: { count: number }) {
  return (
    <aside className="online-players-card" aria-live="polite" aria-label={`${count} player${count === 1 ? "" : "s"} online`}>
      <small>PLAYERS ONLINE</small>
      <strong>{count.toLocaleString()}</strong>
    </aside>
  );
}

function SpecialAbilityIcon({ itemId }: { itemId: string | null }) {
  if (itemId === FREEZE_SPECIAL_ITEM_ID) {
    return <i className="menu-freeze-icon" aria-hidden="true"><Snowflake strokeWidth={3} /></i>;
  }
  if (itemId === SCRAMBLE_SPECIAL_ITEM_ID) {
    return <i className="menu-scramble-icon" aria-hidden="true"><Shuffle strokeWidth={3} /></i>;
  }
  return <i aria-hidden="true"><b /><b /><b /></i>;
}

function MenuScreen({ xp, coinBalance, specialItemCounts, equippedSpecialItemId, account, accountVerified, onlineCount, onQuick, onRanked, onPlayAi, onLearn, onLeaderboard, onShop, onSpecialItemsShop, onEquipSpecialItem, onAccount, onProfile }: {
  xp: number;
  coinBalance: number | null;
  specialItemCounts: SpecialItemCounts;
  equippedSpecialItemId: string | null;
  account: AccountSession | null;
  accountVerified: boolean;
  onlineCount: number;
  onQuick: () => void;
  onRanked: () => void;
  onPlayAi: () => void;
  onLearn: () => void;
  onLeaderboard: () => void;
  onShop: () => void;
  onSpecialItemsShop: () => void;
  onEquipSpecialItem: (itemId: string) => Promise<void>;
  onAccount: () => void;
  onProfile: () => void;
}) {
  const [abilityOpen, setAbilityOpen] = useState(false);
  const [equippingAbilityId, setEquippingAbilityId] = useState<string | null>(null);
  const [abilityError, setAbilityError] = useState(false);
  const rankedReady = Boolean(accountVerified && account?.authenticated && !account.needsUsername && isRankedUnlocked(xp));
  const ownedSpecialItems = SPECIAL_ITEM_CATALOG.filter((item) => (specialItemCounts[item.id] ?? 0) > 0);
  const equippedSpecialItem = SPECIAL_ITEM_CATALOG.find((item) => item.id === equippedSpecialItemId) ?? null;
  const equipAbility = async (itemId: string) => {
    if (equippingAbilityId || itemId === equippedSpecialItemId) return;
    setEquippingAbilityId(itemId);
    setAbilityError(false);
    try {
      await onEquipSpecialItem(itemId);
    } catch {
      setAbilityError(true);
    } finally {
      setEquippingAbilityId(null);
    }
  };
  return (
    <motion.section className={`menu-screen ${ADS_ENABLED ? "with-bottom-ad" : ""}`} initial={false} animate={{ opacity: 1 }}>
      <OnlinePlayersCard count={onlineCount} />
      <div className="menu-account-cluster">
        {account?.authenticated && !account.needsUsername && (
          <>
            <button className="coin-balance-chip menu-coin-chip" onClick={onShop} disabled={!accountVerified} aria-label={account.development ? "Unlimited coins; open shop" : `${coinBalance?.toLocaleString() ?? "Loading"} coins; open shop`}>
              <i aria-hidden="true">◆</i>
              <b>{account.development ? "∞" : coinBalance?.toLocaleString() ?? "…"}</b>
            </button>
            <div className="menu-ability-wrap">
              <button
                className={`menu-ability-chip ${equippedSpecialItem ? "equipped" : "empty"}`}
                onClick={() => setAbilityOpen((open) => !open)}
                disabled={!accountVerified}
                aria-expanded={abilityOpen}
                aria-controls="menu-special-abilities"
                aria-label={equippedSpecialItem ? `${equippedSpecialItem.name} equipped; open special abilities` : "No special ability equipped; open special abilities"}
              >
                <SpecialAbilityIcon itemId={equippedSpecialItemId} />
                <span><small>ABILITY</small><strong>{equippedSpecialItem ? equippedSpecialItem.name.replace("Crafting ", "") : "EMPTY"}</strong></span>
              </button>
              <AnimatePresence>
                {abilityOpen && (
                  <motion.section
                    id="menu-special-abilities"
                    className="menu-ability-panel"
                    aria-label="Special abilities"
                    initial={{ opacity: 0, y: -8, scale: .97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6, scale: .98 }}
                    transition={{ duration: .14 }}
                  >
                    <header><span><small>BATTLE LOADOUT</small><strong>SPECIAL ABILITY</strong></span><button onClick={() => setAbilityOpen(false)} aria-label="Close special abilities">×</button></header>
                    <p>Equip one item to take into battle. It can be used once per match.</p>
                    {ownedSpecialItems.length > 0 ? (
                      <div className="menu-ability-list">
                        {ownedSpecialItems.map((item) => {
                          const equipped = item.id === equippedSpecialItemId;
                          return (
                            <button className={equipped ? "equipped" : ""} key={item.id} onClick={() => void equipAbility(item.id)} disabled={equipped || equippingAbilityId !== null}>
                              <SpecialAbilityIcon itemId={item.id} />
                              <span><strong>{item.name}</strong><small>×{specialItemCounts[item.id]} AVAILABLE</small></span>
                              <em>{equipped ? "EQUIPPED" : equippingAbilityId === item.id ? "EQUIPPING…" : "EQUIP"}</em>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <button className="menu-ability-empty" onClick={() => { setAbilityOpen(false); onSpecialItemsShop(); }}>
                        <strong>NO SPECIAL ITEMS</strong><span>VISIT THE SHOP</span>
                      </button>
                    )}
                    {abilityError && <small className="menu-ability-error" role="alert">COULD NOT EQUIP THAT ITEM.</small>}
                  </motion.section>
                )}
              </AnimatePresence>
            </div>
          </>
        )}
        <button
          className={`account-status-chip ${account?.authenticated && !account.needsUsername ? "signed-in" : "guest"}`}
          onClick={account?.authenticated && !account.needsUsername ? onProfile : onAccount}
          disabled={Boolean(account?.authenticated && !account.needsUsername && !accountVerified)}
        >
          {account?.authenticated && !account.needsUsername && <AvatarFaceplate faceplateId={account.cosmetics.faceplateId} />}
          <span className="account-status-copy"><b>{account?.authenticated && !account.needsUsername ? account.username : "GUEST"}</b>
            <small>{account?.development ? "LOCAL DEV" : account?.authenticated && !account.needsUsername ? "VIEW PROFILE" : "SAVE XP"}</small>
          </span>
        </button>
      </div>
      <div className="menu-main">
        <HomepageLogo />
        <p className="menu-tagline">MINECRAFT CRAFTING COMPETITION</p>
        <ExperienceBar xp={xp} />
        {(!account || account.guest || account.needsUsername) && <button className="guest-progress-callout" onClick={onAccount}><b>PLAYING AS GUEST</b><span>CREATE AN ACCOUNT TO EARN XP</span></button>}
        <div className="menu-actions">
          <MinecraftButton onClick={onQuick}><span>Quick match</span><small>RATED · XP MOVES BOTH WAYS</small></MinecraftButton>
          <MinecraftButton onClick={onRanked} disabled={!accountVerified || Boolean(account?.authenticated && !account.needsUsername && !isRankedUnlocked(xp))}>
            <span>Ranked match</span><small>{!accountVerified ? "RESTORING SESSION" : !account?.authenticated || account.needsUsername ? "ACCOUNT REQUIRED" : rankedReady ? "RATED · CLOSE MATCHUPS" : `LOCKED · ${RANKED_UNLOCK_XP} XP`}</small>
          </MinecraftButton>
          <MinecraftButton onClick={onPlayAi} subtle><span>Play AI</span><small>OFFLINE PRACTICE · CHOOSE DIFFICULTY</small></MinecraftButton>
          <div className="menu-secondary-actions">
            <MinecraftButton onClick={onLeaderboard} subtle disabled={!accountVerified}><span>Leaderboard</span><small>{accountVerified ? "TOP CRAFTERS" : "RESTORING SESSION"}</small></MinecraftButton>
            <MinecraftButton onClick={onShop} subtle disabled={!accountVerified}><span>Shop</span><small>{accountVerified ? "ACCOUNT COSMETICS" : "RESTORING SESSION"}</small></MinecraftButton>
          </div>
          <MinecraftButton onClick={onLearn} subtle><span>Learn recipes</span><small>STUDY EVERY ITEM</small></MinecraftButton>
        </div>
        <LegalLinks />
      </div>
      {ADS_ENABLED && (
        <div className="home-ad-zone">
          <AdSenseSlot placement="home-bottom" slot="5195875521" fixedDesktop />
        </div>
      )}
    </motion.section>
  );
}

function QueueScreen({ state, onPlayAi, onCancel, onRetry }: { state: QueueState; onPlayAi: () => void; onCancel: () => void; onRetry: () => void }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);
  const elapsed = Math.max(0, Math.floor((now - state.joinedAt) / 1_000));
  return (
    <motion.section className="queue-screen" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className={`queue-card ${state.error ? "is-error" : ""}`} aria-live="polite">
        <div className="queue-mode-tab"><MatchModeBadge mode={state.mode} /></div>
        <h1>{state.error ? "QUEUE INTERRUPTED" : state.queueName ? "FINDING A PLAYER" : "JOINING QUEUE"}</h1>
        {state.error && <p>The matchmaker could not be reached. You can retry or return to the menu.</p>}
        {!state.error && (
          <div className="queue-search-status match-mode-badge">
            <span>{state.queueName ? "SEARCHING" : "CONNECTING"}</span>
            <strong>{String(Math.floor(elapsed / 60)).padStart(2, "0")}:{String(elapsed % 60).padStart(2, "0")}</strong>
          </div>
        )}
        <div className="queue-actions">
          {state.error ? <><MinecraftButton onClick={onRetry}>Retry queue</MinecraftButton><MinecraftButton onClick={onPlayAi}>Practice vs AI offline</MinecraftButton></> : <MinecraftButton onClick={onPlayAi}>Play AI while waiting</MinecraftButton>}
          <MinecraftButton onClick={onCancel} subtle>Cancel</MinecraftButton>
        </div>
      </div>
    </motion.section>
  );
}

const AI_DIFFICULTY_OPTIONS: Array<{ difficulty: AiDifficulty; description: string; label: string }> = [
  { difficulty: "easy", label: "Easy", description: "RELAXED · LONGER THINKING" },
  { difficulty: "normal", label: "Normal", description: "BALANCED · HUMAN PACE" },
  { difficulty: "hard", label: "Hard", description: "FAST · SHORT HESITATIONS" },
  { difficulty: "impossible", label: "Impossible", description: "ELITE · NEAR-INSTANT REACTIONS" }
];

export function AiDifficultyDialog({ selected, onChoose, onClose }: {
  selected: AiDifficulty;
  onChoose: (difficulty: AiDifficulty) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  return (
    <div className="ai-difficulty-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <motion.section
        className="ai-difficulty-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ai-difficulty-title"
        initial={{ opacity: 0, scale: .92, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
      >
        <button className="ai-difficulty-close" onClick={onClose} aria-label="Close AI difficulty selector">×</button>
        <h2 id="ai-difficulty-title">CHOOSE DIFFICULTY</h2>
        <p>The bot always knows the recipe, but its thinking and placement speed change each round.</p>
        <div className="ai-difficulty-options">
          {AI_DIFFICULTY_OPTIONS.map((option) => (
            <MinecraftButton
              key={option.difficulty}
              onClick={() => onChoose(option.difficulty)}
              subtle={option.difficulty !== selected}
            >
              <span>{option.label}</span>
              <small>{option.description}{option.difficulty === selected ? " · LAST PLAYED" : ""}</small>
            </MinecraftButton>
          ))}
        </div>
      </motion.section>
    </div>
  );
}

function MatchOfferDialog({ offer, onAccept, onDecline }: { offer: MatchOffer; onAccept: () => void; onDecline: () => void }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 100);
    return () => window.clearInterval(timer);
  }, []);
  const seconds = Math.max(0, Math.ceil((offer.expiresAt - now) / 1_000));
  return (
    <div className="account-dialog-backdrop" role="presentation">
      <section className="account-dialog" role="dialog" aria-modal="true" aria-labelledby="match-offer-title">
        <h2 id="match-offer-title">MATCH FOUND</h2>
        <p>{offer.opponentUsername} is ready. Accept within {seconds} second{seconds === 1 ? "" : "s"}.</p>
        <div className="account-actions">
          <MinecraftButton onClick={onAccept} disabled={seconds === 0}>Accept match</MinecraftButton>
          <MinecraftButton onClick={onDecline} subtle>Keep playing offline</MinecraftButton>
        </div>
      </section>
    </div>
  );
}

function initialScreen(): AppScreen {
  if (typeof window === "undefined") return "menu";
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  if (path === "/about") return "about";
  if (path === "/privacy") return "privacy";
  if (path === "/terms") return "terms";
  return "menu";
}

function RecipeDiagram({ recipe, resolve, compact = false }: { recipe: Recipe; resolve: (id: string) => string; compact?: boolean }) {
  const normalized = useMemo(() => normalizeRecipeGrid(recipe), [recipe]);
  return (
    <div className={`recipe-grid ${compact ? "compact" : ""}`} aria-label={`Recipe for ${recipe.name}`}>
      {normalized.map((item, index) => <div className="recipe-slot" key={index}>{item && <ItemIcon itemId={item} resolve={resolve} />}</div>)}
    </div>
  );
}

export type RecipeCategory = "all" | "blocks" | "tools" | "redstone" | "food" | "misc";

const RECIPE_CATEGORIES: Array<{ id: RecipeCategory; label: string; icon: ItemId }> = [
  { id: "all", label: "All recipes", icon: "crafting_table" },
  { id: "blocks", label: "Building blocks", icon: "bricks" },
  { id: "tools", label: "Tools and gear", icon: "iron_pickaxe" },
  { id: "redstone", label: "Redstone", icon: "redstone" },
  { id: "food", label: "Food", icon: "apple" },
  { id: "misc", label: "Miscellaneous", icon: "chest" }
];

export function categoryForRecipe(recipe: Recipe): Exclude<RecipeCategory, "all"> {
  const id = recipe.outputItemId;
  if (/(redstone|repeater|comparator|piston|observer|dispenser|dropper|hopper|rail|minecart|lever|button|pressure_plate|tripwire_hook|target|daylight_detector|note_block|jukebox|crafter)/.test(id)) return "redstone";
  if (/(apple|bread|cake|cookie|stew|soup|porkchop|beef|chicken|mutton|rabbit|cod|salmon|carrot|potato|melon|pumpkin_pie|honey_bottle|dried_kelp|chorus_fruit|golden_apple|beetroot|mushroom|suspicious_stew)/.test(id)) return "food";
  if (/(_pickaxe|_axe|_shovel|_hoe|_sword|_helmet|_chestplate|_leggings|_boots|bow$|crossbow|shield|trident|mace|elytra|shears|fishing_rod|flint_and_steel|brush|compass|clock|spyglass)/.test(id)) return "tools";
  if (/(planks|wood|log|stem|hyphae|stone|brick|slab|stairs|wall|fence|door|trapdoor|glass|pane|concrete|terracotta|wool|carpet|bed|sandstone|quartz|copper|deepslate|tuff|prismarine|purpur|basalt|blackstone|obsidian|ice|snow|clay|mud|bamboo_mosaic|bookshelf|lantern|lamp|candle|torch|chain)/.test(id)) return "blocks";
  return "misc";
}

export function learnPracticeRecipes(category: RecipeCategory): Recipe[] {
  return category === "all"
    ? [...MATCH_RECIPES]
    : MATCH_RECIPES.filter((recipe) => categoryForRecipe(recipe) === category);
}

export function buildLearnPracticeQueue(category: RecipeCategory, seed: number): Recipe[] {
  return shuffleSeeded(learnPracticeRecipes(category), seed);
}

function learnPracticeSeed(): number {
  const randomValue = typeof globalThis.crypto?.randomUUID === "function"
    ? globalThis.crypto.randomUUID()
    : `${Date.now()}:${Math.random()}`;
  return hashSeed(`learn-practice:${randomValue}`);
}

export function LearnPracticeDialog({ resolve, onChoose, onClose }: {
  resolve: (id: string) => string;
  onChoose: (category: RecipeCategory) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  return (
    <div className="ai-difficulty-backdrop learn-practice-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <motion.section
        className="ai-difficulty-dialog learn-practice-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="learn-practice-title"
        initial={{ opacity: 0, scale: .92, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
      >
        <button className="ai-difficulty-close" onClick={onClose} aria-label="Close practice category selector">×</button>
        <h2 id="learn-practice-title">CHOOSE PRACTICE</h2>
        <p>Craft every Battle item in one category, or take on the complete Battle catalog.</p>
        <div className="learn-practice-options">
          {RECIPE_CATEGORIES.map((option) => {
            const count = learnPracticeRecipes(option.id).length;
            const label = option.id === "all" ? "All Battle Items" : option.label;
            return (
              <button className="practice-category-option" key={option.id} onClick={() => onChoose(option.id)}>
                <ItemIcon itemId={option.icon} resolve={resolve} />
                <span><strong>{label}</strong><small>{count} BATTLE RECIPES</small></span>
              </button>
            );
          })}
        </div>
      </motion.section>
    </div>
  );
}

type LearnPracticeSession = {
  category: RecipeCategory;
  categoryLabel: string;
  recipes: Recipe[];
  index: number;
  seed: number;
  phase: "active" | "correct" | "complete";
};

function LearnScreen({ resolve, playerName, playerLevel, playerLoadout, onBack }: {
  resolve: (id: string) => string;
  playerName: string;
  playerLevel: number;
  playerLoadout: CosmeticLoadout;
  onBack: () => void;
}) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<RecipeCategory>("all");
  const [selectedId, setSelectedId] = useState(RECIPES[0]?.id ?? "");
  const [visibleCount, setVisibleCount] = useState(24);
  const [practiceDialogOpen, setPracticeDialogOpen] = useState(false);
  const [practiceSession, setPracticeSession] = useState<LearnPracticeSession | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const practiceGrid = useGameStore((state) => state.grid);
  const gameStatus = useGameStore((state) => state.status);
  const playKind = useGameStore((state) => state.playKind);
  const configureLearnRecipe = useGameStore((state) => state.configureLearnRecipe);
  const resetMatch = useGameStore((state) => state.resetMatch);
  const filtered = useMemo(() => {
    const search = query.trim().toLowerCase();
    return RECIPES.filter((recipe) => {
      if (category !== "all" && categoryForRecipe(recipe) !== category) return false;
      if (!search) return true;
      const ingredientNames = recipe.grid.filter((item): item is ItemId => Boolean(item)).map(itemName).join(" ");
      return `${recipe.name} ${ingredientNames}`.toLowerCase().includes(search);
    });
  }, [category, query]);
  const selected = filtered.find((recipe) => recipe.id === selectedId) ?? filtered[0] ?? null;
  const counts = selected ? [...ingredientCounts(selected)] : [];
  const categoryLabel = RECIPE_CATEGORIES.find((option) => option.id === category)?.label ?? "All recipes";

  const resetResults = () => setVisibleCount(24);

  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel || visibleCount >= filtered.length) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisibleCount(filtered.length);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry?.isIntersecting) return;
      observer.disconnect();
      setVisibleCount((count) => Math.min(count + 24, filtered.length));
    }, { rootMargin: "240px 0px" });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [filtered.length, visibleCount]);

  useEffect(() => {
    if (!practiceSession || practiceSession.phase !== "active" || playKind !== "learn" || gameStatus !== "round-result") return;
    setPracticeSession((current) => current && current.phase === "active"
      ? { ...current, phase: "correct" }
      : current);
  }, [gameStatus, playKind, practiceSession]);

  useEffect(() => {
    if (!practiceSession || practiceSession.phase !== "correct") return;
    const timer = window.setTimeout(() => {
      const nextIndex = practiceSession.index + 1;
      if (nextIndex >= practiceSession.recipes.length) {
        setPracticeSession((current) => current && current.seed === practiceSession.seed
          ? { ...current, phase: "complete" }
          : current);
        return;
      }
      configureLearnRecipe(practiceSession.recipes[nextIndex]!, practiceSession.seed, nextIndex + 1);
      setPracticeSession((current) => current && current.seed === practiceSession.seed
        ? { ...current, index: nextIndex, phase: "active" }
        : current);
    }, 800);
    return () => window.clearTimeout(timer);
  }, [configureLearnRecipe, practiceSession]);

  const startPractice = useCallback((practiceCategory: RecipeCategory) => {
    const seed = learnPracticeSeed();
    const recipes = buildLearnPracticeQueue(practiceCategory, seed);
    const categoryLabel = practiceCategory === "all"
      ? "All Battle Items"
      : RECIPE_CATEGORIES.find((option) => option.id === practiceCategory)?.label ?? "Battle Items";
    if (!recipes[0]) return;
    setPracticeDialogOpen(false);
    setPracticeSession({ category: practiceCategory, categoryLabel, recipes, index: 0, seed, phase: "active" });
    configureLearnRecipe(recipes[0], seed, 1);
  }, [configureLearnRecipe]);

  const returnToRecipeBook = useCallback(() => {
    resetMatch();
    setPracticeSession(null);
    setPracticeDialogOpen(false);
  }, [resetMatch]);

  const exitLearn = useCallback(() => {
    resetMatch();
    onBack();
  }, [onBack, resetMatch]);

  if (practiceSession) {
    if (practiceSession.phase === "complete") {
      const categoryIcon = RECIPE_CATEGORIES.find((option) => option.id === practiceSession.category)?.icon ?? "crafting_table";
      return (
        <motion.section className="learn-screen learn-practice-screen" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <header className="learn-header">
            <button className="exit-button" onClick={exitLearn}>Exit</button>
          </header>
          <div className="learn-practice-complete">
            <ItemIcon itemId={categoryIcon} resolve={resolve} large />
            <small>{practiceSession.categoryLabel.toUpperCase()}</small>
            <h1>PRACTICE COMPLETE</h1>
            <strong>{practiceSession.recipes.length} RECIPES CRAFTED</strong>
            <div>
              <MinecraftButton onClick={() => startPractice(practiceSession.category)}>Practice again</MinecraftButton>
              <MinecraftButton onClick={returnToRecipeBook} subtle>Recipe book</MinecraftButton>
            </div>
          </div>
        </motion.section>
      );
    }

    const currentRecipe = practiceSession.recipes[practiceSession.index]!;
    const referenceTableId = DEFAULT_COSMETIC_LOADOUT.craftingTableId;
    return (
      <motion.section className="learn-screen learn-practice-screen" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <header className="learn-header">
          <div className="learn-header-actions">
            <button className="exit-button" onClick={returnToRecipeBook}>Recipe book</button>
            <button className="exit-button" onClick={exitLearn}>Exit</button>
          </div>
        </header>
        <div className="learn-practice-play-area">
          <div className="learn-practice-heading">
            <span className="match-mode-badge learn-practice-badge">{practiceSession.categoryLabel.toUpperCase()}</span>
            <strong className="match-mode-badge learn-practice-badge" aria-label={`Recipe ${practiceSession.index + 1} of ${practiceSession.recipes.length}`}>
              {practiceSession.index + 1} / {practiceSession.recipes.length}
            </strong>
          </div>
          <div className="learn-practice-stage">
            <section
              className={`duelist-card learn-player-table table-theme-${cosmeticThemeClass(playerLoadout.craftingTableId)}`}
              style={cosmeticTableStyle(playerLoadout.craftingTableId)}
            >
              <DuelistName playerName={playerName} faceplateId={playerLoadout.faceplateId} detail={`LEVEL ${playerLevel}`} />
              <CraftGrid grid={practiceGrid} resolve={resolve} interactive={practiceSession.phase === "active" && gameStatus === "active"} />
            </section>
            <section
              className={`duelist-card learn-reference-table table-theme-${cosmeticThemeClass(referenceTableId)}`}
              style={cosmeticTableStyle(referenceTableId)}
            >
              <div className="learn-reference-output">
                <ItemIcon itemId={currentRecipe.outputItemId} resolve={resolve} large />
                <span><small>CRAFT</small><strong>{currentRecipe.name}</strong></span>
              </div>
              <RecipeDiagram recipe={currentRecipe} resolve={resolve} />
            </section>
          </div>
          <Inventory resolve={resolve} />
        </div>
      </motion.section>
    );
  }

  return (
    <>
      <motion.section className="learn-screen" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <header className="learn-header">
          <div className="learn-header-actions">
            <button className="exit-button learn-practice-button" onClick={() => setPracticeDialogOpen(true)}>Practice</button>
            <button className="exit-button" onClick={exitLearn}>Exit</button>
          </div>
        </header>
        <div className="learn-toolbar">
          <label className="recipe-search">
            <span>SEARCH</span>
            <input
              value={query}
              onChange={(event) => { setQuery(event.target.value); resetResults(); }}
              placeholder={`Find one of ${RECIPES.length} recipes...`}
            />
          </label>
          <div className="recipe-tabs" aria-label="Recipe book categories">
            {RECIPE_CATEGORIES.map((option) => (
              <button
                className={category === option.id ? "active" : ""}
                key={option.id}
                onClick={() => { setCategory(option.id); resetResults(); }}
                aria-label={option.label}
                aria-pressed={category === option.id}
                title={option.label}
              >
                <ItemIcon itemId={option.icon} resolve={resolve} />
              </button>
            ))}
          </div>
        </div>
        <div className="learn-content">
          <aside className="recipe-study-card" aria-live="polite">
            {selected ? (
              <>
                <div className="study-output"><ItemIcon itemId={selected.outputItemId} resolve={resolve} large /><div><small>CRAFT</small><h1>{selected.name}</h1></div></div>
                <RecipeDiagram recipe={selected} resolve={resolve} />
                <div className="study-stats">
                  <span>{selected.shaped ? "SHAPED" : "SHAPELESS"}</span>
                  <span>COMPLEXITY {selected.difficulty}/5</span>
                  <span>MAKES {selected.outputQuantity}</span>
                </div>
                <div className="ingredient-list">
                  <b>INGREDIENTS</b>
                  {counts.map(([itemId, amount]) => (
                    <div key={itemId}><ItemIcon itemId={itemId} resolve={resolve} /><span>{itemName(itemId)}</span><strong>×{amount}</strong></div>
                  ))}
                </div>
                <p className="study-note">The pattern can be moved anywhere it fits in the 3×3 grid. Shaped recipes may also be mirrored.</p>
              </>
            ) : <p className="empty-recipes">No recipes match that search.</p>}
          </aside>
          <section className="recipe-library">
            <div className="library-heading"><strong>{categoryLabel.toUpperCase()}</strong><span>{filtered.length} FOUND</span></div>
            <div className="recipe-card-grid">
              {filtered.slice(0, visibleCount).map((recipe) => (
                <button className={`recipe-library-card ${selected?.id === recipe.id ? "selected" : ""}`} key={recipe.id} onClick={() => setSelectedId(recipe.id)}>
                  <ItemIcon itemId={recipe.outputItemId} resolve={resolve} />
                  <span><b>{recipe.name}</b><small>{recipe.grid.filter(Boolean).length} ITEMS · LEVEL {recipe.difficulty}</small></span>
                </button>
              ))}
            </div>
            {visibleCount < filtered.length && (
              <div className="recipe-sentinel" ref={loadMoreRef} aria-label="More recipes load automatically when scrolled into view"><i /><span>LOADING MORE RECIPES</span></div>
            )}
          </section>
        </div>
      </motion.section>
      {practiceDialogOpen && <LearnPracticeDialog resolve={resolve} onChoose={startPractice} onClose={() => setPracticeDialogOpen(false)} />}
    </>
  );
}

function Timer({ seconds, preparing }: { seconds: number; preparing: boolean }) {
  const warning = !preparing && seconds <= 5;
  const warningColors = ["#ff3434", "#ff3434", "#ff5638", "#ff863d", "#ffb943", "#ffe16a"];
  const timerColor = warning ? warningColors[seconds] : preparing ? "#fff2a8" : "#f4f4f4";
  return (
    <div
      className={`battle-timer ${preparing ? "preparing" : ""} ${warning ? "danger" : ""} ${warning && seconds <= 3 ? "critical" : ""}`}
      style={{ "--timer-color": timerColor } as CSSProperties}
      aria-label={`${seconds} seconds remaining`}
    >
      <strong className="timer-value" key={seconds} aria-hidden="true">{seconds}</strong>
    </div>
  );
}

function TopHud({ onExit, mode, playerFaceplateId, rivalFaceplateId, seconds, preparing = false, searching = false, waiting = false }: {
  onExit: () => void;
  mode: MatchMode;
  playerFaceplateId: string;
  rivalFaceplateId: string;
  seconds?: number;
  preparing?: boolean;
  searching?: boolean;
  waiting?: boolean;
}) {
  const { round, score } = useGameStore();
  return (
    <header className="battle-hud">
      <div className={`match-identity ${searching || waiting ? "has-match-status" : ""}`}>
        <Logo />
        {searching ? (
          <span className="match-mode-badge match-status-badge" aria-live="polite">SEARCHING FOR MATCH</span>
        ) : waiting ? (
          <span className="match-mode-badge match-status-badge" aria-live="polite">WAITING FOR OPPONENT</span>
        ) : (
          <MatchModeBadge mode={mode} />
        )}
      </div>
      <div className="score-box with-timer" aria-label={`Score ${score[0]} to ${score[1]}`}>
        <div className="score-side player-score">
          <AvatarFaceplate faceplateId={playerFaceplateId} label="Your faceplate" />
          <span className="score-value"><strong>{score[0]}</strong></span>
        </div>
        {waiting ? (
          <p><b>WAITING</b><span>FOR OPPONENT</span></p>
        ) : preparing ? (
          <p className="hud-ready"><b>GET READY</b></p>
        ) : seconds === undefined ? (
          <p className="hud-round"><b>ROUND {round}</b><span>FIRST TO {ROUNDS_TO_WIN}</span></p>
        ) : (
          <div className="hud-countdown active-round-timer">
            <Timer seconds={seconds} preparing={false} />
          </div>
        )}
        <div className="score-side rival-score">
          <span className="score-value"><strong>{score[1]}</strong></span>
          <AvatarFaceplate faceplateId={rivalFaceplateId} label="Rival faceplate" />
        </div>
      </div>
      <button className="exit-button" onClick={onExit}>Exit</button>
    </header>
  );
}

function CraftGrid({ grid, resolve, interactive, hintShape }: { grid: Grid; resolve: (id: string) => string; interactive: boolean; hintShape?: boolean[] }) {
  const { selectedItem, draggingItem, beginDrag, endDrag, placeSelected, clearSlot, clearAllOfItem, soundEnabled } = useGameStore();
  const suppressClick = useRef(new Set<number>());
  const paintedSlots = useRef(new Set<number>());
  const lastRemoval = useRef<{ index: number; itemId: ItemId; time: number } | null>(null);
  const lastPointerDownAt = useRef(0);

  const paintSlot = useCallback((index: number, item: ItemId) => {
    placeSelected(index, item);
    playTone("place", soundEnabled);
    haptic("place");
  }, [placeSelected, soundEnabled]);

  const paintDraggedSlot = useCallback((index: number) => {
    const state = useGameStore.getState();
    if (!state.draggingItem || state.grid[index] || paintedSlots.current.has(index)) return;
    paintedSlots.current.add(index);
    paintSlot(index, state.draggingItem);
  }, [paintSlot]);

  const beginPaintingSlot = (index: number, item: ItemId) => {
    if (paintedSlots.current.has(index)) return;
    if (useGameStore.getState().draggingItem !== item) beginDrag(item);
    paintedSlots.current.add(index);
    paintSlot(index, item);
  };

  useEffect(() => {
    const stopDragging = () => {
      paintedSlots.current.clear();
      endDrag();
    };
    const continueDraggingAt = (clientX: number, clientY: number) => {
      if (!useGameStore.getState().draggingItem) return;
      const slot = document
        .elementFromPoint(clientX, clientY)
        ?.closest<HTMLButtonElement>("[data-grid-slot]");
      if (!slot || slot.disabled) return;
      const index = Number(slot.dataset.gridSlot);
      if (Number.isInteger(index)) paintDraggedSlot(index);
    };
    const continuePointerDragging = (event: PointerEvent) => {
      if (!useGameStore.getState().draggingItem) return;
      event.preventDefault();
      continueDraggingAt(event.clientX, event.clientY);
    };
    const continueTouchDragging = (event: TouchEvent) => {
      if (!useGameStore.getState().draggingItem) return;
      event.preventDefault();
      const touch = event.touches.item(0);
      if (touch) continueDraggingAt(touch.clientX, touch.clientY);
    };
    const stopPointerDragging = (event: PointerEvent) => {
      // Touch Events are the fallback authority for direct-manipulation input.
      // Waiting for touchend avoids ending the drag if WebKit cancels its pointer stream.
      if (event.pointerType !== "touch") stopDragging();
    };
    window.addEventListener("pointermove", continuePointerDragging, { passive: false });
    window.addEventListener("pointerup", stopPointerDragging);
    window.addEventListener("pointercancel", stopPointerDragging);
    window.addEventListener("touchmove", continueTouchDragging, { passive: false });
    window.addEventListener("touchend", stopDragging);
    window.addEventListener("touchcancel", stopDragging);
    return () => {
      window.removeEventListener("pointermove", continuePointerDragging);
      window.removeEventListener("pointerup", stopPointerDragging);
      window.removeEventListener("pointercancel", stopPointerDragging);
      window.removeEventListener("touchmove", continueTouchDragging);
      window.removeEventListener("touchend", stopDragging);
      window.removeEventListener("touchcancel", stopDragging);
    };
  }, [endDrag, paintDraggedSlot]);

  const recentRemoval = (index: number) => {
    const removal = lastRemoval.current;
    return removal && removal.index === index && Date.now() - removal.time <= DOUBLE_ACTIVATION_WINDOW_MS
      ? removal
      : null;
  };

  return (
    <div className={`craft-grid ${draggingItem ? "drag-painting" : ""}`} aria-label="Your crafting grid">
      {grid.map((item, index) => (
        <button
          className={`craft-slot ${item ? "filled" : ""} ${hintShape?.[index] ? "shape-hint" : ""}`}
          key={index}
          data-grid-slot={index}
          disabled={!interactive}
          aria-label={item
            ? `${itemName(item)}, remove from slot; Shift-click or double-tap to remove all matching items`
            : hintShape?.[index]
              ? `Hinted crafting slot ${index + 1}; ingredient unknown`
              : `Empty crafting slot ${index + 1}`}
          onPointerDown={(event) => {
            // A new physical press makes any unused marker from the previous
            // gesture stale. The click synthesized for this press has no extra
            // pointerdown, so a newly added marker survives until that click.
            suppressClick.current.clear();
            lastPointerDownAt.current = Date.now();
            if (recentRemoval(index)) {
              event.preventDefault();
              return;
            }
            if (event.button !== 0 || item || !selectedItem) return;
            event.preventDefault();
            suppressClick.current.add(index);
            beginPaintingSlot(index, selectedItem);
          }}
          onTouchStart={(event) => {
            // Pointer Events normally precede this Touch Event. Only clear here
            // for older touch-only engines where no pointerdown was delivered.
            if (Date.now() - lastPointerDownAt.current > 50) suppressClick.current.clear();
            if (recentRemoval(index)) {
              event.preventDefault();
              return;
            }
            if (item || !selectedItem) return;
            event.preventDefault();
            suppressClick.current.add(index);
            beginPaintingSlot(index, selectedItem);
          }}
          onPointerEnter={() => paintDraggedSlot(index)}
          onPointerMove={() => paintDraggedSlot(index)}
          onClick={(event) => {
            if (suppressClick.current.delete(index)) return;
            const repeatedRemoval = event.detail > 0 ? recentRemoval(index) : null;
            if (repeatedRemoval) {
              lastRemoval.current = null;
              clearAllOfItem(repeatedRemoval.itemId);
              playTone("place", soundEnabled);
              haptic("place");
              return;
            }
            if (item) {
              if (event.shiftKey) clearAllOfItem(item);
              else {
                clearSlot(index);
                lastRemoval.current = { index, itemId: item, time: Date.now() };
              }
              playTone("place", soundEnabled);
              haptic("place");
              return;
            }
            if (selectedItem) placeSelected(index);
            playTone("place", soundEnabled);
            haptic(selectedItem ? "place" : "select");
          }}
        >
          {item && <ItemIcon itemId={item} resolve={resolve} />}
        </button>
      ))}
    </div>
  );
}

function Inventory({ resolve, masked = false, frozen = false }: { resolve: (id: string) => string; masked?: boolean; frozen?: boolean }) {
  const { inventory, grid, selectedItem, draggingItem, beginDrag, selectItem, status, soundEnabled, inventoryScrambleRevision } = useGameStore();
  if (masked) {
    const slotCount = Math.max(10, inventory.length);
    return (
      <div className="inventory masked-inventory" aria-label="Inventory hidden while waiting for opponent">
        {Array.from({ length: slotCount }, (_, index) => (
          <button className="inventory-slot mystery" key={index} disabled aria-label={`Mystery inventory slot ${index + 1}`}>
            <MysteryBlock />
          </button>
        ))}
      </div>
    );
  }
  return (
    <div
      key={`inventory-${inventoryScrambleRevision}`}
      className={`inventory ${frozen ? "frozen" : ""} ${inventoryScrambleRevision > 0 ? "scrambling" : ""}`}
      aria-label={frozen ? "Inventory frozen" : "Inventory"}
    >
      {inventory.map((slot) => {
        const remaining = availableQuantity(inventory, grid, slot.itemId);
        const startDragging = () => {
          if (useGameStore.getState().draggingItem === slot.itemId) return;
          beginDrag(slot.itemId);
          playTone("pick", soundEnabled);
          haptic("select");
        };
        return (
          <button
            className={`inventory-slot ${selectedItem === slot.itemId ? "selected" : ""} ${draggingItem === slot.itemId ? "dragging" : ""} ${remaining === 0 ? "depleted" : ""}`}
            key={slot.slotId}
            data-item-id={slot.itemId}
            disabled={frozen || status !== "active" || remaining === 0}
            onPointerDown={(event) => {
              if (event.button !== 0) return;
              event.preventDefault();
              startDragging();
            }}
            onTouchStart={(event) => {
              event.preventDefault();
              startDragging();
            }}
            onClick={(event) => {
              if (event.detail !== 0) return;
              selectItem(slot.itemId);
              playTone("pick", soundEnabled);
            }}
            aria-label={`${itemName(slot.itemId)}, ${remaining} available`}
          >
            <ItemIcon itemId={slot.itemId} resolve={resolve} />
            <strong>{remaining}</strong>
          </button>
        );
      })}
    </div>
  );
}

function DuelistName({ playerName, faceplateId, detail }: { playerName: string; faceplateId: string; detail: string }) {
  return (
    <div className="duelist-name">
      <AvatarFaceplate faceplateId={faceplateId} label={`${playerName}'s avatar`} />
      <span className="duelist-name-copy">
        <b>{playerName}</b>
        <small>{detail}</small>
      </span>
    </div>
  );
}

function RivalPanel({ grid, playerName, playerLevel, loadout, frozen = false }: { grid: Grid; playerName: string; playerLevel: number | null; loadout: CosmeticLoadout; frozen?: boolean }) {
  return (
    <section className={`duelist-card rival-card ${frozen ? "frozen" : ""} table-theme-${cosmeticThemeClass(loadout.craftingTableId)}`} style={cosmeticTableStyle(loadout.craftingTableId)}>
      <DuelistName playerName={playerName} faceplateId={loadout.faceplateId} detail={playerLevel === null ? "AI BOT" : `LEVEL ${playerLevel}`} />
      <div className="rival-grid" aria-label="Rival recipe hidden">
        {grid.map((item, index) => (
          <div
            className={`masked-slot ${item ? "occupied" : ""}`}
            key={index}
            aria-label={item ? `Rival placed an item in slot ${index + 1}; item hidden` : `Rival slot ${index + 1} is empty`}
          >
            <AnimatePresence>
              {item && (
                <motion.span
                  className="concealed-item"
                  aria-hidden="true"
                  initial={{ opacity: 0, scale: .55, y: -8 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                >
                  <b>?</b>
                </motion.span>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </section>
  );
}

function MatchupAbility({ itemId }: { itemId: string | null }) {
  const item = SPECIAL_ITEM_CATALOG.find((candidate) => candidate.id === itemId);
  if (!item) return null;
  return (
    <motion.div
      className="matchup-ability"
      initial={{ opacity: 0, y: 8, scale: .9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: .48, duration: .2 }}
      aria-label={`${item.name} equipped`}
    >
      <SpecialAbilityIcon itemId={item.id} />
      <span><small>ABILITY</small><b>{item.name}</b></span>
    </motion.div>
  );
}

function MatchupReveal({ playerName, playerLevel, rivalName, rivalLevel, playerFaceplateId, rivalFaceplateId, playerSpecialItemId, rivalSpecialItemId }: {
  playerName: string;
  playerLevel: number;
  rivalName: string;
  rivalLevel: number | null;
  playerFaceplateId: string;
  rivalFaceplateId: string;
  playerSpecialItemId: string | null;
  rivalSpecialItemId: string | null;
}) {
  return (
    <motion.div
      className="matchup-reveal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: .9, y: 18 }}
      transition={{ duration: .22 }}
    >
      <div className="matchup-lineup">
        <motion.article
          className="matchup-player matchup-player-self"
          initial={{ opacity: 0, x: -140, scale: .72 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          transition={{ type: "spring", stiffness: 230, damping: 19, delay: .08 }}
        >
          <AvatarFaceplate faceplateId={playerFaceplateId} label={`${playerName}'s faceplate`} />
          <motion.div className="matchup-player-copy" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .32 }}>
            <strong>{playerName}</strong>
            <span>LEVEL {playerLevel}</span>
            <MatchupAbility itemId={playerSpecialItemId} />
          </motion.div>
        </motion.article>
        <motion.div
          className="matchup-versus"
          aria-hidden="true"
          initial={{ opacity: 0, scale: .25, rotate: -25 }}
          animate={{ opacity: 1, scale: 1, rotate: -8 }}
          transition={{ type: "spring", stiffness: 350, damping: 17, delay: .25 }}
        >
          <span>X</span>
        </motion.div>
        <motion.article
          className="matchup-player matchup-player-rival"
          initial={{ opacity: 0, x: 140, scale: .72 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          transition={{ type: "spring", stiffness: 230, damping: 19, delay: .08 }}
        >
          <AvatarFaceplate faceplateId={rivalFaceplateId} label={`${rivalName}'s faceplate`} />
          <motion.div className="matchup-player-copy" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .32 }}>
            <strong>{rivalName}</strong>
            <span>{rivalLevel === null ? "FORGE BOT" : `LEVEL ${rivalLevel}`}</span>
            <MatchupAbility itemId={rivalSpecialItemId} />
          </motion.div>
        </motion.article>
      </div>
    </motion.div>
  );
}

function AdSenseSlot({ placement, slot, fixedDesktop = false }: { placement: string; slot: string; fixedDesktop?: boolean }) {
  if (!ADS_ENABLED) return null;
  return <EnabledAdSenseSlot placement={placement} slot={slot} fixedDesktop={fixedDesktop} />;
}

function EnabledAdSenseSlot({ placement, slot, fixedDesktop = false }: { placement: string; slot: string; fixedDesktop?: boolean }) {
  const [placementEligible, setPlacementEligible] = useState(!fixedDesktop);
  const [clientReady, setClientReady] = useState(false);
  const [adStatus, setAdStatus] = useState<"loading" | "filled" | "unfilled">("loading");
  const adRequested = useRef(false);
  const adElement = useRef<HTMLModElement | null>(null);

  useEffect(() => {
    if (!fixedDesktop) return;
    setPlacementEligible(window.matchMedia("(min-width: 800px)").matches);
  }, [fixedDesktop]);

  useEffect(() => {
    if (!placementEligible || !canLoadAdSense()) {
      setAdStatus("unfilled");
      return;
    }
    setAdStatus("loading");
    let active = true;
    void loadAdSense()
      .then(() => {
        if (active) setClientReady(true);
      })
      .catch((error) => {
        if (!active) return;
        setAdStatus("unfilled");
        clientWarn("adsense_script_initialization_failed", {
          placement,
          message: error instanceof Error ? error.message : "Unknown AdSense error"
        });
      });
    return () => {
      active = false;
    };
  }, [placement, placementEligible]);

  useEffect(() => {
    if (!clientReady || !adElement.current) return;
    const element = adElement.current;
    const updateStatus = () => {
      const status = element.dataset.adStatus;
      if (status === "filled") setAdStatus("filled");
      else if (status === "unfilled" || status === "unfill-optimized") setAdStatus("unfilled");
    };
    const observer = new MutationObserver(updateStatus);
    observer.observe(element, { attributes: true, attributeFilter: ["data-ad-status"] });
    updateStatus();
    return () => observer.disconnect();
  }, [clientReady]);

  useEffect(() => {
    if (!clientReady || adRequested.current) return;
    adRequested.current = true;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (error) {
      clientWarn("adsense_unit_initialization_failed", {
        placement,
        message: error instanceof Error ? error.message : "Unknown AdSense error"
      });
    }
  }, [clientReady, placement]);

  return (
    <aside
      className={`battle-ad-frame ad-${adStatus}`}
      data-ad-placement={placement}
      data-ad-size={fixedDesktop ? "728x90" : undefined}
      aria-label={adStatus === "filled" ? "Advertisement" : undefined}
      aria-hidden={adStatus !== "filled"}
    >
      <span className="ad-label">ADVERTISEMENT</span>
      <div className="ad-mount">
        {clientReady && (
          <ins
            ref={adElement}
            className="adsbygoogle"
            style={fixedDesktop ? { display: "inline-block", width: 728, height: 90 } : { display: "block" }}
            data-ad-client={ADSENSE_CLIENT}
            data-ad-slot={slot}
            data-ad-format={fixedDesktop ? undefined : "horizontal"}
            data-full-width-responsive={fixedDesktop ? undefined : "false"}
          />
        )}
      </div>
    </aside>
  );
}

function BattleScreen({ resolve, onExit, mode, seconds, preparing, searching, frozen, playerName, playerLevel, rivalName, rivalLevel, playerLoadout, rivalLoadout, playerSpecialItemId, rivalSpecialItemId, masterEligible, blueprintCount, freezeCount, scrambleCount, specialUsed, onUseBlueprint, onUseFreeze, onUseScramble }: {
  resolve: (id: string) => string;
  onExit: () => void;
  mode: MatchMode;
  seconds: number;
  preparing: boolean;
  searching: boolean;
  frozen: boolean;
  playerName: string;
  playerLevel: number;
  rivalName: string;
  rivalLevel: number | null;
  playerLoadout: CosmeticLoadout;
  rivalLoadout: CosmeticLoadout;
  playerSpecialItemId: string | null;
  rivalSpecialItemId: string | null;
  masterEligible: boolean;
  blueprintCount: number | null;
  freezeCount: number | null;
  scrambleCount: number | null;
  specialUsed: boolean;
  onUseBlueprint: () => Promise<boolean>;
  onUseFreeze: () => Promise<boolean>;
  onUseScramble: () => Promise<boolean>;
}) {
  const { recipe, grid, rivalGrid, round, status, playKind } = useGameStore();
  const [hintedRound, setHintedRound] = useState<number | null>(null);
  const [usingBlueprint, setUsingBlueprint] = useState(false);
  const [usingFreeze, setUsingFreeze] = useState(false);
  const [usingScramble, setUsingScramble] = useState(false);
  const [blueprintFailed, setBlueprintFailed] = useState(false);
  const [freezeFailed, setFreezeFailed] = useState(false);
  const [scrambleFailed, setScrambleFailed] = useState(false);
  const waitingForOpponent = playKind === "online" && status === "ready";
  const displayedPlayerGrid: Grid = waitingForOpponent ? grid.map(() => null) : grid;
  const displayedRivalGrid: Grid = waitingForOpponent ? rivalGrid.map(() => null) : rivalGrid;
  const blueprintActive = hintedRound === round;
  const hintShape = useMemo(
    () => blueprintActive ? recipeShapeHint(recipe) : undefined,
    [blueprintActive, recipe]
  );
  const activateBlueprint = async () => {
    if (usingBlueprint || blueprintActive || specialUsed || blueprintCount === null || blueprintCount <= 0 || status !== "active") return;
    setUsingBlueprint(true);
    setBlueprintFailed(false);
    const requestedRound = round;
    setHintedRound(requestedRound);
    const consumed = await onUseBlueprint();
    if (!consumed && useGameStore.getState().round === requestedRound && useGameStore.getState().status === "active") {
      setHintedRound(null);
      setBlueprintFailed(true);
    }
    setUsingBlueprint(false);
  };
  const activateFreeze = async () => {
    if (usingFreeze || frozen || specialUsed || freezeCount === null || freezeCount <= 0 || status !== "active") return;
    setUsingFreeze(true);
    setFreezeFailed(false);
    if (!await onUseFreeze()) setFreezeFailed(true);
    setUsingFreeze(false);
  };
  const activateScramble = async () => {
    if (usingScramble || specialUsed || scrambleCount === null || scrambleCount <= 0 || status !== "active") return;
    setUsingScramble(true);
    setScrambleFailed(false);
    if (!await onUseScramble()) setScrambleFailed(true);
    setUsingScramble(false);
  };
  const introPhase = battleIntroPhase(round, seconds);
  const introLabel = introPhase.kind === "matchup"
    ? `Match found: ${playerName} versus ${rivalName}`
    : introPhase.kind === "round"
      ? `Round ${round}`
      : `${introPhase.seconds}`;
  return (
    <motion.section className={`battle-screen ${preparing ? "is-preparing" : ""} ${ADS_ENABLED ? "with-bottom-ad" : ""}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <TopHud onExit={onExit} mode={mode} playerFaceplateId={playerLoadout.faceplateId} rivalFaceplateId={rivalLoadout.faceplateId} seconds={waitingForOpponent ? undefined : seconds} preparing={preparing} searching={searching} waiting={waitingForOpponent} />
      {preparing && (
        <div className={`round-start-overlay phase-${introPhase.kind}`} aria-live="assertive" aria-label={introLabel}>
          <AnimatePresence mode="wait">
            {introPhase.kind === "matchup" ? (
              <MatchupReveal
                key={introPhase.key}
                playerName={playerName}
                playerLevel={playerLevel}
                rivalName={rivalName}
                rivalLevel={rivalLevel}
                playerFaceplateId={playerLoadout.faceplateId}
                rivalFaceplateId={rivalLoadout.faceplateId}
                playerSpecialItemId={playerSpecialItemId}
                rivalSpecialItemId={rivalSpecialItemId}
              />
            ) : (
              <motion.strong
                className={introPhase.kind === "round" ? "round-start-title" : `round-start-countdown ${introPhase.seconds <= 2 ? "urgent" : ""}`}
                key={introPhase.key}
                initial={{ opacity: 0, scale: .72 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.18 }}
                transition={{ duration: .18 }}
              >
                {introPhase.kind === "round" ? `ROUND ${round}` : introPhase.seconds}
              </motion.strong>
            )}
          </AnimatePresence>
        </div>
      )}
      <div className="battle-stage">
        <div className="battlefield">
          <div className="battle-target">
            {waitingForOpponent ? <MysteryBlock /> : <ItemIcon itemId={recipe.outputItemId} resolve={resolve} large />}
            {!waitingForOpponent && masterEligible && (recipe.difficulty === 4 || recipe.difficulty === 5) && (
              <span className="master-time-target">MASTER TIME · {recipe.difficulty === 4 ? "5.0" : "7.0"} SEC</span>
            )}
          </div>
          <section className={`duelist-card player-card ${frozen ? "frozen" : ""} table-theme-${cosmeticThemeClass(playerLoadout.craftingTableId)}`} style={cosmeticTableStyle(playerLoadout.craftingTableId)}>
            <DuelistName playerName={playerName} faceplateId={playerLoadout.faceplateId} detail={`LEVEL ${playerLevel}`} />
            <CraftGrid grid={displayedPlayerGrid} resolve={resolve} interactive={status === "active" && !frozen} hintShape={hintShape} />
          </section>
          <div className={`duel-divider ${blueprintCount !== null || freezeCount !== null || scrambleCount !== null || specialUsed ? "has-special" : ""}`}>
            <div className="versus-badge" aria-hidden="true">VS</div>
              <AnimatePresence initial={false}>
              {blueprintCount !== null && !specialUsed && (
                <motion.button
                  key={CRAFTING_BLUEPRINT_ID}
                  className={`battle-special-item ${blueprintActive ? "active" : ""}`}
                  disabled={waitingForOpponent || status !== "active" || usingBlueprint || blueprintActive || blueprintCount <= 0}
                  onClick={() => void activateBlueprint()}
                  aria-label={blueprintActive ? "Crafting Blueprint active for this round" : `Use Crafting Blueprint, ${blueprintCount} available`}
                  initial={{ opacity: 0, scale: .82, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0, filter: "brightness(1)" }}
                  exit={{ opacity: 0, scale: .58, y: 20, filter: "brightness(2)" }}
                  transition={{ duration: .32, ease: [0.22, 1, 0.36, 1] }}
                >
                  <i aria-hidden="true"><b /><b className="hinted" /><b /><b /><b className="hinted" /><b /><b /><b className="hinted" /><b /></i>
                  <span><strong>SHAPE HINT</strong><small>{blueprintActive ? "ACTIVE" : usingBlueprint ? "USING…" : blueprintFailed ? "TRY AGAIN" : `×${blueprintCount}`}</small></span>
                </motion.button>
              )}
              {freezeCount !== null && !specialUsed && (
                <motion.button
                  key={FREEZE_SPECIAL_ITEM_ID}
                  className="battle-special-item freeze-item"
                  disabled={waitingForOpponent || status !== "active" || usingFreeze || frozen || freezeCount <= 0}
                  onClick={() => void activateFreeze()}
                  aria-label={`Use Freeze, ${freezeCount} available`}
                  initial={{ opacity: 0, scale: .82, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0, filter: "brightness(1)" }}
                  exit={{ opacity: 0, scale: .58, y: 20, filter: "brightness(2)" }}
                  transition={{ duration: .32, ease: [0.22, 1, 0.36, 1] }}
                >
                  <i className="freeze-special-icon" aria-hidden="true"><Snowflake strokeWidth={3} /></i>
                  <span><strong>TIME FREEZE</strong><small>{usingFreeze ? "FREEZING…" : freezeFailed ? "TRY AGAIN" : `×${freezeCount}`}</small></span>
                </motion.button>
              )}
              {scrambleCount !== null && !specialUsed && (
                <motion.button
                  key={SCRAMBLE_SPECIAL_ITEM_ID}
                  className="battle-special-item scramble-item"
                  disabled={waitingForOpponent || status !== "active" || usingScramble || scrambleCount <= 0}
                  onClick={() => void activateScramble()}
                  aria-label={`Use Scramble, ${scrambleCount} available`}
                  initial={{ opacity: 0, scale: .82, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0, filter: "brightness(1)" }}
                  exit={{ opacity: 0, scale: .58, y: 20, filter: "brightness(2)" }}
                  transition={{ duration: .32, ease: [0.22, 1, 0.36, 1] }}
                >
                  <i className="scramble-special-icon" aria-hidden="true"><Shuffle strokeWidth={3} /></i>
                  <span><strong>SCRAMBLE</strong><small>{usingScramble ? "SHUFFLING…" : scrambleFailed ? "TRY AGAIN" : `×${scrambleCount}`}</small></span>
                </motion.button>
              )}
              </AnimatePresence>
          </div>
          <RivalPanel grid={displayedRivalGrid} playerName={rivalName} playerLevel={rivalLevel} loadout={rivalLoadout} frozen={frozen} />
        </div>
        <Inventory resolve={resolve} masked={waitingForOpponent} frozen={frozen} />
      </div>
      {ADS_ENABLED && (
        <div className="battle-ad-zone">
          <AdSenseSlot placement={`${mode}-battle-bottom`} slot="7877879188" fixedDesktop />
        </div>
      )}
    </motion.section>
  );
}

function RecipeGrid({ resolve }: { resolve: (id: string) => string }) {
  const recipe = useGameStore((state) => state.recipe);
  return <RecipeDiagram recipe={recipe} resolve={resolve} />;
}

function RoundResultScreen({ resolve, onExit, mode, playerFaceplateId, rivalFaceplateId, nextRoundProgress, nextRoundSeconds, masterCraft }: {
  resolve: (id: string) => string;
  onExit: () => void;
  mode: MatchMode;
  playerFaceplateId: string;
  rivalFaceplateId: string;
  nextRoundProgress: number;
  nextRoundSeconds: number;
  masterCraft: MasterCraftResult | null;
}) {
  const { recipe, winner } = useGameStore();
  const playerWon = winner === "player";
  const resultLabel = winner === "draw" ? "ROUND DRAW" : playerWon ? "ROUND WON" : "ROUND LOST";
  return (
    <motion.section className="result-screen" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <TopHud onExit={onExit} mode={mode} playerFaceplateId={playerFaceplateId} rivalFaceplateId={rivalFaceplateId} />
      <div className="result-content">
        <div className={`result-banner round-result-banner ${playerWon ? "win" : "loss"}`}>
          <h1>{resultLabel}</h1>
        </div>
        {masterCraft && (
          <div className="master-craft-banner" aria-live="polite">
            <strong>MASTER CRAFT</strong>
            <span>{(masterCraft.adjustedElapsedMs / 1_000).toFixed(2)} SEC · +5 COINS PENDING</span>
          </div>
        )}
        <div className="result-card">
          <div className="result-output"><ItemIcon itemId={recipe.outputItemId} resolve={resolve} large /><h2>{recipe.name}</h2></div>
          <div className="result-arrow" aria-hidden="true"><img src={resolve("item.arrow")} alt="" draggable={false} /></div>
          <RecipeGrid resolve={resolve} />
        </div>
        <div className="round-transition">
          <div className="round-transition-copy">
            <span>NEXT ROUND</span>
            <strong>{nextRoundSeconds > 0 ? `${nextRoundSeconds}S` : "STARTING"}</strong>
          </div>
          <div
            className="experience-track round-transition-track"
            role="progressbar"
            aria-label="Time until the next round"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(nextRoundProgress)}
          >
            <i style={{ width: `${nextRoundProgress}%` }} />
          </div>
        </div>
      </div>
    </motion.section>
  );
}

function MatchXpScreen({ onExit, onRematch, mode, playerFaceplateId, rivalFaceplateId, xp, xpChange, rivalXp, noXpLabel, coinReward }: { onExit: () => void; onRematch: () => void; mode: MatchMode; playerFaceplateId: string; rivalFaceplateId: string; xp: number; xpChange: number | null; rivalXp: number; noXpLabel: string | null; coinReward: CoinRewardResult | null }) {
  const { score, winner } = useGameStore();
  const playerWon = score[0] >= ROUNDS_TO_WIN;
  const matchLabel = winner === "draw" ? "MATCH DRAW" : playerWon ? "MATCH WON" : "MATCH LOST";
  const effectiveNoXpLabel = winner === "draw" ? "DRAW" : noXpLabel;
  const previousXp = xpChange === null ? xp : Math.max(0, xp - xpChange);
  const [displayXp, setDisplayXp] = useState(previousXp);
  const trackMaximum = Math.max(RANKED_UNLOCK_XP, Math.ceil(Math.max(previousXp, xp, 1) / RANKED_UNLOCK_XP) * RANKED_UNLOCK_XP);
  const progress = Math.min(100, Math.max(0, (displayXp / trackMaximum) * 100));
  const adjustmentClass = xpChange === null || xpChange === 0 ? "floor" : xpChange > 0 ? "gain" : "loss";
  const adjustmentLabel = effectiveNoXpLabel
    ? `NO XP · ${effectiveNoXpLabel}`
    : xpChange === null
      ? "CALCULATING XP"
      : xpChange > 0
        ? `+${xpChange} XP`
        : xpChange < 0
          ? `${xpChange} XP`
          : "0 XP · FLOOR PROTECTED";

  useEffect(() => {
    const startXp = xpChange === null ? xp : Math.max(0, xp - xpChange);
    if (xpChange === null || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setDisplayXp(xp);
      return;
    }
    setDisplayXp(startXp);
    const startedAt = performance.now();
    const duration = 1_200;
    let frame = 0;
    const animate = (now: number) => {
      const elapsed = Math.min(1, (now - startedAt) / duration);
      const eased = 1 - Math.pow(1 - elapsed, 3);
      setDisplayXp(Math.round(startXp + (xp - startXp) * eased));
      if (elapsed < 1) frame = window.requestAnimationFrame(animate);
    };
    frame = window.requestAnimationFrame(animate);
    return () => window.cancelAnimationFrame(frame);
  }, [xp, xpChange]);

  return (
    <motion.section className="result-screen xp-result-screen" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <TopHud onExit={onExit} mode={mode} playerFaceplateId={playerFaceplateId} rivalFaceplateId={rivalFaceplateId} />
      <div className="xp-result-content">
        <div className={`result-banner round-result-banner ${playerWon ? "win" : "loss"}`}>
          <h1>{matchLabel}</h1>
        </div>
        <section className={`xp-result-card ${adjustmentClass}`} aria-live="polite">
          <div className="xp-result-heading">
            <strong className={adjustmentClass}>{adjustmentLabel}</strong>
          </div>
          <div className="xp-result-total"><strong>{displayXp.toLocaleString()}</strong><span> XP</span></div>
          <div className="xp-result-track" aria-label={`${displayXp} of ${trackMaximum} experience points`}>
            <i style={{ width: `${progress}%` }} />
          </div>
          <div className="xp-result-meta"><span>0 XP</span><span>RIVAL {rivalXp.toLocaleString()} XP</span><span>{trackMaximum.toLocaleString()} XP</span></div>
        </section>
        {coinReward && (coinReward.coinsGranted > 0 || coinReward.masterCraftsRecognized > 0) && (
          <section className="match-coin-reward" aria-live="polite">
            <strong>◆ {coinReward.coinsGranted > 0 ? `+${coinReward.coinsGranted} COINS` : "MASTER CRAFT RECORDED"}</strong>
            {coinReward.xpMilestones.length > 0 && <span>XP MILESTONE: {coinReward.xpMilestones.map((value) => value.toLocaleString()).join(", ")} XP</span>}
            {coinReward.masterCraftsRecognized > 0 && (
              <span>{coinReward.masterCraftsRecognized} MASTER CRAFT{coinReward.masterCraftsRecognized === 1 ? "" : "S"} · {coinReward.masterCraftsToday}/{coinReward.masterCraftDailyLimit} REWARDED TODAY</span>
            )}
            <small>WALLET BALANCE · ◆ {coinReward.coinBalance.toLocaleString()}</small>
          </section>
        )}
        <div className="xp-result-actions">
          <MinecraftButton onClick={onRematch}>Play again</MinecraftButton>
          <MinecraftButton onClick={onExit} subtle>Menu</MinecraftButton>
        </div>
      </div>
    </motion.section>
  );
}

export function App() {
  const { resolve } = useAssets();
  const { status, round, deadline, durationMs, frozenUntil, soundEnabled, startRound, resetMatch, configureAiMatch, tick, playKind } = useGameStore();
  const [screen, setScreen] = useState<AppScreen>(initialScreen);
  const staticScreen = screen === "about" || screen === "privacy" || screen === "terms";
  const { count: onlineCount } = useOnlinePlayers(!staticScreen);
  const [matchMode, setMatchMode] = useState<MatchMode>("quick");
  const [initialAccount] = useState(loadInitialAccount);
  const [xp, setXp] = useState(() => initialAccount.session?.xp ?? 0);
  const [account, setAccount] = useState<AccountSession | null>(initialAccount.session);
  const [accountVerified, setAccountVerified] = useState(initialAccount.source === "server");
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [pendingAccountAction, setPendingAccountAction] = useState<AccountAction | null>(null);
  const [shopInitialCategory, setShopInitialCategory] = useState<ShopCategory>("all");
  const [rivalXp, setRivalXp] = useState(0);
  const [xpChange, setXpChange] = useState<number | null>(null);
  const [coinBalance, setCoinBalance] = useState<number | null>(null);
  const [specialItemCounts, setSpecialItemCounts] = useState<SpecialItemCounts>({});
  const [equippedSpecialItemId, setEquippedSpecialItemId] = useState<string | null>(null);
  const [specialUsedThisBattle, setSpecialUsedThisBattle] = useState(false);
  const [coinReward, setCoinReward] = useState<CoinRewardResult | null>(null);
  const [roundMasterCraft, setRoundMasterCraft] = useState<MasterCraftResult | null>(null);
  const [queueState, setQueueState] = useState<QueueState | null>(null);
  const [onlineMatch, setOnlineMatch] = useState<MatchedResult | null>(null);
  const [aiDifficultyDialogSource, setAiDifficultyDialogSource] = useState<"menu" | "queue" | null>(null);
  const [selectedAiDifficulty, setSelectedAiDifficulty] = useState<AiDifficulty>("normal");
  const [now, setNow] = useState(Date.now());
  const lastCountdownSecond = useRef<number | null>(null);
  const onlineSocket = useRef<WebSocket | null>(null);
  const onlineSequence = useRef(0);
  const onlineActions = useRef(new OnlineActionTracker());
  const youIndex = useRef(0);
  const selfPlayerId = useRef<string | null>(null);
  const progressionFallbackTimer = useRef<number | null>(null);
  const battleAbilityID = useRef(newBattleAbilityID());

  useEffect(() => {
    if (!isNativeBuild) return;
    const handleNativeBack = () => {
      if (accountDialogOpen) {
        setAccountDialogOpen(false);
        return;
      }
      if (profileOpen) {
        setProfileOpen(false);
        return;
      }
      if (screen === "battle") {
        exitBattle();
        return;
      }
      if (screen !== "menu") setScreen("menu");
    };
    window.addEventListener("craftoff:back", handleNativeBack);
    return () => window.removeEventListener("craftoff:back", handleNativeBack);
  });

  const closeOnlineSocket = useCallback((forfeit = false) => {
    const socket = onlineSocket.current;
    onlineSocket.current = null;
    onlineActions.current.clear();
    if (socket && socket.readyState < WebSocket.CLOSING) {
      if (forfeit && socket.readyState === WebSocket.OPEN) {
        try {
          socket.send(encodeForfeit());
        } catch (error: unknown) {
          clientWarn("match_forfeit_send_failed", { message: error instanceof Error ? error.message : "unknown" });
        }
      }
      clientLog("match_socket_close_requested", { readyState: socket.readyState, forfeit });
      socket.close(forfeit ? MATCH_FORFEIT_CLOSE_CODE : 1000, forfeit ? "Player exited match" : "Leaving match");
    }
  }, []);

  const forfeitOnlineMatchOnPageExit = useCallback(() => {
    const match = onlineMatch;
    const game = useGameStore.getState();
    if (!match || !onlineSocket.current || game.playKind !== "online" || game.status === "match-result") return;
    const body = JSON.stringify({ matchId: match.matchId, token: match.token });
    let beaconQueued = false;
    try {
      beaconQueued = !isNativeBuild && navigator.sendBeacon(
        apiUrl("/api/match/forfeit"),
        new Blob([body], { type: "application/json" })
      );
    } catch {
      // The socket forfeit below remains the primary fallback.
    }
    if (!beaconQueued) {
      void fetch(apiUrl("/api/match/forfeit"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true
      }).catch(() => undefined);
    }
    closeOnlineSocket(true);
  }, [closeOnlineSocket, onlineMatch]);

  const sendOnlineAction = useCallback((action: OnlineAction) => {
    const socket = onlineSocket.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      const queued = onlineActions.current.enqueue(action);
      if (queued) {
        clientWarn("online_action_queued", { action: action.type, readyState: socket?.readyState ?? -1, pendingCount: onlineActions.current.queuedCount });
      } else {
        clientLog("online_action_suppressed", { action: action.type, reason: "grid_slot_pending", gridSlot: action.type === "MOVE_ITEM" ? action.toSlot : action.type === "REMOVE_ITEM" ? action.slot : undefined });
      }
      return;
    }
    if (!onlineActions.current.canSchedule(action)) {
      clientLog("online_action_suppressed", { action: action.type, reason: "grid_slot_pending", gridSlot: action.type === "MOVE_ITEM" ? action.toSlot : action.type === "REMOVE_ITEM" ? action.slot : undefined });
      return;
    }
    const sequence = onlineSequence.current = onlineSequence.current % 0xffff + 1;
    onlineActions.current.track(sequence, action);
    try {
      socket.send(action.type === "REMOVE_ITEM"
        ? encodeRemoveItem(sequence, action.slot)
        : encodePlaceItem(sequence, action.fromSlot, action.toSlot));
      onlineActions.current.resolve(sequence);
    } catch (error: unknown) {
      onlineActions.current.resolve(sequence);
      clientError("online_action_send_failed", { action: action.type, sequence, message: error instanceof Error ? error.message : "unknown" });
      return;
    }
    clientLog("online_action_sent", {
      action: action.type,
      sequence,
      inventorySlot: action.type === "MOVE_ITEM" ? action.fromSlot : undefined,
      gridSlot: action.type === "MOVE_ITEM" ? action.toSlot : action.type === "REMOVE_ITEM" ? action.slot : undefined
    });
  }, []);

  const connectMatchedPlayer = useCallback((match: MatchedResult, mode: MatchMode, promotedSocket: WebSocket) => {
    closeOnlineSocket();
    setAiDifficultyDialogSource(null);
    setQueueState(null);
    setOnlineMatch(match);
    setMatchMode(mode);
    setRivalXp(match.opponentXp);
    setXpChange(null);
    setCoinReward(null);
    setRoundMasterCraft(null);
    battleAbilityID.current = newBattleAbilityID(match.matchId);
    setSpecialUsedThisBattle(false);
    onlineSequence.current = 0;
    onlineActions.current.clear();
    youIndex.current = 0;
    selfPlayerId.current = null;

    const url = new URL(isNativeBuild
      ? webSocketUrl(`/ws/match/${match.matchId}`, DIRECT_GAMEPLAY_ORIGIN || nativeApiOrigin)
      : new URL(`/ws/match/${match.matchId}`, DIRECT_GAMEPLAY_ORIGIN || window.location.href).toString());
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    url.searchParams.set("token", match.token);
    let reconnectAttempts = 0;
    const connectSocket = (existingSocket?: WebSocket) => {
      const socket = existingSocket ?? new WebSocket(url, MATCH_BINARY_SUBPROTOCOL);
      socket.binaryType = "arraybuffer";
      onlineSocket.current = socket;
      const connectTimeout = window.setTimeout(() => {
        if (onlineSocket.current === socket && socket.readyState === WebSocket.CONNECTING) socket.close();
      }, 1_500);
      clientLog("match_socket_connecting", { matchId: match.matchId, mode, opponentRating: match.opponentRating, reconnectAttempts });

      const connected = () => {
        window.clearTimeout(connectTimeout);
        const queued = onlineActions.current.drain();
        clientLog("match_socket_connected", { matchId: match.matchId, queuedActionCount: queued.length, reconnectAttempts });
        if (socket.readyState === WebSocket.OPEN) socket.send(encodeStateRequest());
        queued.forEach(sendOnlineAction);
      };
      socket.addEventListener("open", connected);
      socket.addEventListener("error", () => {
        clientError("match_socket_error", { matchId: match.matchId, readyState: socket.readyState, reconnectAttempts });
      });
      socket.addEventListener("close", (event) => {
        window.clearTimeout(connectTimeout);
        clientLog("match_socket_closed", { matchId: match.matchId, code: event.code, reason: event.reason, clean: event.wasClean, reconnectAttempts });
        if (onlineSocket.current !== socket || event.wasClean || event.code !== 1006 || reconnectAttempts >= 4) return;
        reconnectAttempts += 1;
        onlineActions.current.clear();
        const delayMs = 250 * (2 ** (reconnectAttempts - 1));
        window.setTimeout(() => {
          if (onlineSocket.current === socket) connectSocket();
        }, delayMs);
      });
      socket.addEventListener("message", (event) => {
      const store = useGameStore.getState();
      const orientScore = (serverScore: [number, number]): [number, number] => youIndex.current === 1
        ? [serverScore[1], serverScore[0]]
        : [serverScore[0], serverScore[1]];
      const masterCraftForUI = (craft: { playerIndex: number; round: number; recipeId: string; adjustedElapsedMs: number; thresholdMs: number } | null): MasterCraftResult | null => {
        if (!craft) return null;
        return {
          playerId: craft.playerIndex === youIndex.current ? "__self__" : "__rival__",
          round: craft.round,
          recipeId: craft.recipeId,
          difficulty: craft.thresholdMs === 5_000 ? 4 : 5,
          rawElapsedMs: craft.adjustedElapsedMs + 200,
          latencyAdjustmentMs: 200,
          adjustedElapsedMs: craft.adjustedElapsedMs,
          thresholdMs: craft.thresholdMs
        };
      };
      if (event.data instanceof ArrayBuffer) {
        const message = decodeBinaryMatchMessage(event.data);
        if (!message) {
          clientWarn("match_socket_message_ignored", { matchId: match.matchId, reason: "invalid_binary_message", byteLength: event.data.byteLength });
          return;
        }
        if (message.type === "COMMAND_REJECTED") {
          onlineActions.current.resolve(message.sequence);
          if (socket.readyState === WebSocket.OPEN) socket.send(encodeStateRequest());
          clientWarn("match_server_rejection", { matchId: match.matchId, sequence: message.sequence, reason: message.reason });
          return;
        }
        if (message.type === "OPPONENT_OCCUPANCY") {
          const occupiedItem = store.inventory[0]?.itemId ?? "barrier";
          const rivalGrid = Array.from({ length: 9 }, (_, slot) =>
            message.mask & (1 << slot) ? occupiedItem : null
          );
          store.updateOnlinePlayer(false, rivalGrid);
          return;
        }
        if (message.type === "SPECIAL_FREEZE_STARTED") {
          store.applyFreeze(message.freezeEndsAt, message.deadline);
          return;
        }
        if (message.type === "SPECIAL_SCRAMBLE_STARTED") {
          if (message.actorIndex !== youIndex.current) store.applyScramble(message.order);
          return;
        }
        if (message.type === "MATCH_STATE") {
          const state = message.state;
          youIndex.current = state.youIndex;
          onlineSequence.current = state.lastSequence;
          onlineActions.current.clear();
          store.updateOnlinePlayer(true, state.grids[state.youIndex]!);
          store.updateOnlinePlayer(false, state.grids[state.youIndex === 0 ? 1 : 0]!);
          const recipe = state.recipeId ? RECIPES.find((candidate) => candidate.id === state.recipeId) : undefined;
          if (recipe && state.status === "countdown" && state.roundStartsAt) store.startOnlineCountdown(state.round, recipe, state.inventory, state.roundStartsAt);
          else if (recipe && state.status === "active" && state.deadline) store.startOnlineRound(state.round, recipe, state.inventory, state.deadline);
          else if (state.status === "intermission" && state.completedRecipeId && state.intermissionEndsAt) {
            const completedRecipe = RECIPES.find((candidate) => candidate.id === state.completedRecipeId);
            const winner = state.intermissionWinnerIndex === null ? "draw" : state.intermissionWinnerIndex === state.youIndex ? "player" : "rival";
            if (completedRecipe) store.startOnlineIntermission(state.round, completedRecipe, winner, orientScore(state.score), state.intermissionEndsAt);
          } else if (state.status === "complete") {
            const oriented = orientScore(state.score);
            const winner = oriented[0] >= ROUNDS_TO_WIN ? "player" : oriented[1] >= ROUNDS_TO_WIN ? "rival" : "draw";
            store.finishOnlineRound(winner, oriented, true);
          }
          return;
        }
        if (message.type === "ROUND_STARTING" || message.type === "ROUND_STARTED") {
          const recipe = RECIPES.find((candidate) => candidate.id === message.recipeId);
          if (!recipe) return;
          if (message.type === "ROUND_STARTING") store.startOnlineCountdown(message.round, recipe, message.inventory, message.at);
          else store.startOnlineRound(message.round, recipe, message.inventory, message.at);
          return;
        }
        if (message.type === "ROUND_ENDED") {
          onlineActions.current.clear();
          const oriented = orientScore(message.score);
          const winner = message.winnerIndex === null ? "draw" : message.winnerIndex === youIndex.current ? "player" : "rival";
          const completedRecipe = RECIPES.find((candidate) => candidate.id === message.recipeId);
          const craft = masterCraftForUI(message.masterCraft);
          setRoundMasterCraft(craft?.playerId === "__self__" ? craft : null);
          if (completedRecipe) store.startOnlineIntermission(message.round, completedRecipe, winner, oriented, message.intermissionEndsAt);
          return;
        }
        if (message.type === "MATCH_ENDED") {
          onlineActions.current.clear();
          const oriented = orientScore(message.score);
          const winner = message.winnerIndex === null ? "draw" : message.winnerIndex === youIndex.current ? "player" : "rival";
          const craft = message.masterCrafts.map(masterCraftForUI).find((candidate) => candidate?.playerId === "__self__") ?? null;
          setRoundMasterCraft(craft);
          store.finishOnlineRound(winner, oriented, true);
          const eligible = Boolean(account?.authenticated && !account.needsUsername && message.pendingProgression);
          if (eligible) {
            const minimumVersion = (account?.progressionVersion ?? 0) + 1;
            progressionFallbackTimer.current = window.setTimeout(() => {
              progressionFallbackTimer.current = null;
              void loadCanonicalProgress(minimumVersion).then((canonical) => {
                if (!canonical.current) return;
                setXp((previous) => {
                  setXpChange(canonical.xp - previous);
                  return canonical.xp;
                });
                setAccount((current) => current ? { ...current, xp: canonical.xp, rankedRating: canonical.rankedRating, progressionVersion: canonical.version } : current);
                setCoinBalance(canonical.coinBalance);
                if (canonical.coinReward) setCoinReward(canonical.coinReward);
              }).catch(() => clientWarn("canonical_progress_pending", { matchId: match.matchId }));
            }, 5_000);
          } else setXpChange(0);
          return;
        }
        if (message.type === "PROGRESSION_APPLIED") {
          if (progressionFallbackTimer.current !== null) window.clearTimeout(progressionFallbackTimer.current);
          progressionFallbackTimer.current = null;
          setXp((previous) => {
            setXpChange(message.xp - previous);
            return message.xp;
          });
          setAccount((current) => current ? {
            ...current,
            xp: message.xp,
            rankedRating: message.rankedRating,
            progressionVersion: message.progressionVersion
          } : current);
          setCoinReward(message.coinReward);
          setCoinBalance(message.coinBalance);
          return;
        }
        if (message.type === "MATCH_ABORTED") {
          closeOnlineSocket();
          setOnlineMatch(null);
          store.resetMatch();
          setScreen("menu");
        }
        return;
      }
      if (typeof event.data !== "string") {
        clientWarn("match_socket_message_ignored", { matchId: match.matchId, reason: "unsupported_message" });
        return;
      }
      let message: MatchSocketMessage;
      try {
        message = JSON.parse(event.data) as MatchSocketMessage;
      } catch {
        clientWarn("match_socket_message_ignored", { matchId: match.matchId, reason: "invalid_json", byteLength: event.data.length });
        return;
      }
      clientLog("match_socket_message_received", { matchId: match.matchId, messageType: message.type ?? "unknown", sequence: message.sequence });
      if (message.type === "MATCH_STATE" && message.state && "matchId" in message.state) {
        const state = message.state;
        youIndex.current = state.youIndex;
        onlineSequence.current = state.lastSequence ?? 0;
        onlineActions.current.clear();
        selfPlayerId.current = state.players[state.youIndex]?.id ?? null;
        const ownGrid = state.players[state.youIndex]?.grid ?? Array.from({ length: 9 }, () => null);
        const rivalGrid = state.players[state.youIndex === 0 ? 1 : 0]?.grid ?? Array.from({ length: 9 }, () => null);
        store.updateOnlinePlayer(true, ownGrid);
        store.updateOnlinePlayer(false, rivalGrid);
        const recipe = state.recipeId ? RECIPES.find((candidate) => candidate.id === state.recipeId) : undefined;
        if (recipe && state.status === "countdown" && state.roundStartsAt) store.startOnlineCountdown(state.round, recipe, state.inventory, state.roundStartsAt);
        else if (recipe && state.status === "active" && state.deadline) store.startOnlineRound(state.round, recipe, state.inventory, state.deadline);
        else if (state.status === "intermission" && state.completedRecipeId && state.intermissionEndsAt) {
          const completedRecipe = RECIPES.find((candidate) => candidate.id === state.completedRecipeId);
          const winner = state.intermissionWinnerId === null ? "draw" : state.intermissionWinnerId === selfPlayerId.current ? "player" : "rival";
          if (completedRecipe) store.startOnlineIntermission(state.round, completedRecipe, winner, orientScore(state.score), state.intermissionEndsAt);
        }
        else if (state.status === "complete") {
          const oriented = orientScore(state.score);
          const winner = oriented[0] >= ROUNDS_TO_WIN ? "player" : oriented[1] >= ROUNDS_TO_WIN ? "rival" : "draw";
          store.finishOnlineRound(winner, oriented, true);
        }
        return;
      }

      if ((message.type === "ROUND_STARTING" || message.type === "ROUND_STARTED") && message.recipeId && message.inventory && message.round) {
        const recipe = RECIPES.find((candidate) => candidate.id === message.recipeId);
        if (!recipe) return;
        if (message.type === "ROUND_STARTING" && message.startsAt) store.startOnlineCountdown(message.round, recipe, message.inventory, message.startsAt);
        else if (message.type === "ROUND_STARTED" && message.deadline) store.startOnlineRound(message.round, recipe, message.inventory, message.deadline);
        return;
      }

      if (message.type === "MOVE_ACCEPTED" && message.state && "grid" in message.state) {
        if (typeof message.sequence === "number") onlineActions.current.resolve(message.sequence);
        store.acceptOnlineMove(message.state.grid, message.state.inventory);
        return;
      }
      if (message.type === "MOVE_REJECTED" || message.type === "ERROR") {
        if (typeof message.sequence === "number") onlineActions.current.resolve(message.sequence);
        clientWarn("match_server_rejection", { matchId: match.matchId, messageType: message.type, sequence: message.sequence, reason: message.reason ?? message.code ?? "unknown" });
        return;
      }
      if (message.type === "PLAYER_UPDATED" && message.playerId && message.grid) {
        store.updateOnlinePlayer(message.playerId === selfPlayerId.current, message.grid);
        return;
      }
      if (message.type === "PROGRESSION_APPLIED" && typeof message.xp === "number" && typeof message.progressionVersion === "number") {
        if (progressionFallbackTimer.current !== null) window.clearTimeout(progressionFallbackTimer.current);
        progressionFallbackTimer.current = null;
        setXp((previous) => {
          setXpChange(message.xp! - previous);
          return message.xp!;
        });
        setAccount((current) => current ? {
          ...current,
          xp: message.xp!,
          rankedRating: message.rankedRating ?? current.rankedRating,
          progressionVersion: message.progressionVersion!
        } : current);
        if (message.coinReward) {
          setCoinReward(message.coinReward);
          setCoinBalance(message.coinReward.coinBalance);
        } else if (typeof message.coinBalance === "number") setCoinBalance(message.coinBalance);
        return;
      }
      if (message.type === "ROUND_ENDED" && message.score && message.recipeId && message.intermissionEndsAt) {
        onlineActions.current.clear();
        const oriented = orientScore(message.score);
        const winner = message.winnerId === null ? "draw" : message.winnerId === selfPlayerId.current ? "player" : "rival";
        const completedRecipe = RECIPES.find((candidate) => candidate.id === message.recipeId);
        setRoundMasterCraft(message.masterCraft?.playerId === selfPlayerId.current ? message.masterCraft : null);
        if (completedRecipe) store.startOnlineIntermission(store.round, completedRecipe, winner, oriented, message.intermissionEndsAt);
        return;
      }
      if (message.type === "MATCH_ENDED" && message.score) {
        onlineActions.current.clear();
        const oriented = orientScore(message.score);
        const winner = message.winnerId === null ? "draw" : message.winnerId === selfPlayerId.current ? "player" : "rival";
        const finalMasterCraft = message.masterCrafts?.find((candidate) => candidate.playerId === selfPlayerId.current) ?? null;
        setRoundMasterCraft(finalMasterCraft);
        store.finishOnlineRound(winner, oriented, true);
        const eligible = Boolean(account?.authenticated && !account.needsUsername && message.pendingProgression);
        if (eligible) {
          const minimumVersion = (account?.progressionVersion ?? 0) + 1;
          progressionFallbackTimer.current = window.setTimeout(() => {
            progressionFallbackTimer.current = null;
            void loadCanonicalProgress(minimumVersion).then((canonical) => {
              if (!canonical.current) return;
              setXp((previous) => {
                setXpChange(canonical.xp - previous);
                return canonical.xp;
              });
              setAccount((current) => current ? { ...current, xp: canonical.xp, rankedRating: canonical.rankedRating, progressionVersion: canonical.version } : current);
              setCoinBalance(canonical.coinBalance);
              if (canonical.coinReward) setCoinReward(canonical.coinReward);
            }).catch(() => clientWarn("canonical_progress_pending", { matchId: match.matchId }));
          }, 5_000);
        } else setXpChange(0);
        return;
      }
      if (message.type === "MATCH_ABORTED") {
        closeOnlineSocket();
        setOnlineMatch(null);
        store.resetMatch();
        setScreen("menu");
      }
      });
      if (socket.readyState === WebSocket.OPEN) queueMicrotask(connected);
    };
    // Matchmaking stays behind the Worker; only the long-lived game socket
    // bypasses Worker and Caddy when a dedicated gameplay origin is configured.
    connectSocket(DIRECT_GAMEPLAY_ORIGIN ? undefined : promotedSocket);

    useGameStore.getState().configureOnlineMatch(sendOnlineAction);
    setScreen("battle");
    clientLog("online_match_opened", { matchId: match.matchId, mode, opponentRating: match.opponentRating });
  }, [account, closeOnlineSocket, sendOnlineAction]);

  const matchmaker = useMatchmaking({ onState: setQueueState, onMatched: connectMatchedPlayer });

  useEffect(() => {
    if (matchmaker.offer) setAiDifficultyDialogSource(null);
  }, [matchmaker.offer]);

  const startMatchmaking = useCallback((mode: MatchMode) => {
    if (mode === "ranked" && !isRankedUnlocked(xp)) {
      clientWarn("matchmaking_start_ignored", { mode, xp, reason: "ranked_locked" });
      return;
    }
    clientLog("matchmaking_started", { mode, xp });
    closeOnlineSocket();
    setAiDifficultyDialogSource(null);
    setOnlineMatch(null);
    resetMatch();
    setMatchMode(mode);
    setXpChange(null);
    setScreen("queue");
    matchmaker.start(mode, equippedSpecialItemId);
  }, [closeOnlineSocket, equippedSpecialItemId, matchmaker, resetMatch, xp]);

  useEffect(() => {
    if (staticScreen) return;
    let cancelled = false;
    void (async () => {
      try {
        let session = initialAccount.source === "server" && initialAccount.session
          ? initialAccount.session
          : await loadAccountSession();
        if (session.authenticated && session.needsUsername) {
          const pending = pendingAccountClaim();
          if (pending) session = await claimAccountProfile(pending.username);
        }
        if (cancelled) return;
        session = { ...session, cosmetics: normalizeCosmeticLoadout(session.cosmetics) };
        setAccount(session);
        setAccountVerified(true);
        setXp(session.xp);
        if (session.authenticated && session.needsUsername) setAccountDialogOpen(true);

      } catch {
        // The standalone Vite server has no Worker API; local guest play remains available.
      }
    })();
    return () => { cancelled = true; };
  }, [initialAccount, staticScreen]);

  useEffect(() => {
    if (accountVerified && account) saveAccountPresentation(account);
  }, [account, accountVerified]);

  useEffect(() => {
    const handlePresentationChange = (event: StorageEvent) => {
      if (event.key !== ACCOUNT_PRESENTATION_KEY || event.newValue !== null) return;
      setAccountVerified(false);
      setAccount(null);
      void loadAccountSession().then((session) => {
        const normalized = { ...session, cosmetics: normalizeCosmeticLoadout(session.cosmetics) };
        setAccount(normalized);
        setAccountVerified(true);
        setXp(normalized.xp);
      }).catch(() => undefined);
    };
    window.addEventListener("storage", handlePresentationChange);
    return () => window.removeEventListener("storage", handlePresentationChange);
  }, []);

  useEffect(() => () => closeOnlineSocket(), [closeOnlineSocket]);
  useEffect(() => {
    const handlePageExit = (event: Event) => {
      if (event.type === "pagehide" && (event as PageTransitionEvent).persisted) return;
      forfeitOnlineMatchOnPageExit();
    };
    window.addEventListener("beforeunload", handlePageExit);
    window.addEventListener("pagehide", handlePageExit);
    return () => {
      window.removeEventListener("beforeunload", handlePageExit);
      window.removeEventListener("pagehide", handlePageExit);
    };
  }, [forfeitOnlineMatchOnPageExit]);
  useEffect(() => () => {
    if (progressionFallbackTimer.current !== null) window.clearTimeout(progressionFallbackTimer.current);
  }, []);

  useEffect(() => {
    if (screen === "battle" && status === "ready") startRound();
  }, [screen, status, startRound]);

  useEffect(() => {
    if (screen !== "battle" || (status !== "countdown" && status !== "active" && status !== "round-result")) return;
    const id = window.setInterval(() => {
      const current = Date.now();
      setNow(current);
      tick(current);
    }, 50);
    return () => window.clearInterval(id);
  }, [screen, status, tick]);

  const preparing = status === "countdown";
  const phaseDuration = preparing
    ? round === 1 ? MATCH_START_SEQUENCE_MS : ROUND_START_SEQUENCE_MS
    : durationMs;
  const frozen = status === "active" && (frozenUntil ?? 0) > now;
  const timerNow = frozen ? frozenUntil! : now;
  const remaining = (preparing || status === "active") && deadline ? Math.max(0, deadline - timerNow) : 0;
  const seconds = Math.ceil(Math.min(phaseDuration, remaining) / 1000);
  const roundTransition = roundTransitionState(status === "round-result" ? deadline : null, now);

  useEffect(() => {
    if (screen !== "battle") {
      lastCountdownSecond.current = null;
      return;
    }
    if (preparing && seconds >= 1 && seconds <= 5 && seconds !== lastCountdownSecond.current) {
      const playbackRates = [1, 1.34, 1.26, 1.17, 1.08, 1];
      playAssetSound(resolve("sound.countdown_tick"), soundEnabled, playbackRates[seconds], 0.55);
      lastCountdownSecond.current = seconds;
      return;
    }
    if (!preparing && status === "active" && lastCountdownSecond.current !== null) {
      playAssetSound(resolve("sound.countdown_go"), soundEnabled, 1, 0.5);
      lastCountdownSecond.current = null;
    }
  }, [preparing, resolve, screen, seconds, soundEnabled, status]);

  const revealed = status === "round-result" || status === "match-result";
  const textureStyles = {
    "--panorama": `url(${resolve("ui.panorama")})`,
    "--menu-texture": `url(${resolve("ui.menu_background")})`,
    "--dirt-texture": `url(${resolve("ui.dirt")})`,
    "--button-texture": `url(${resolve("ui.button")})`,
    "--button-hover-texture": `url(${resolve("ui.button_hover")})`,
    "--slot-texture": `url(${resolve("ui.inventory_slot")})`
  } as CSSProperties;

  const startAiMatch = (difficulty: AiDifficulty) => {
    const startingFromMenu = aiDifficultyDialogSource === "menu";
    const aiMatchMode = startingFromMenu ? "quick" : queueState?.mode ?? "quick";
    if (startingFromMenu) {
      matchmaker.cancel();
      closeOnlineSocket();
      setQueueState(null);
      setOnlineMatch(null);
    }
    setSelectedAiDifficulty(difficulty);
    setAiDifficultyDialogSource(null);
    configureAiMatch(difficulty);
    const nextMatchSeed = useGameStore.getState().matchSeed;
    setRivalXp(selectRivalXp(xp, nextMatchSeed, aiMatchMode));
    setXpChange(null);
    setCoinReward(null);
    setRoundMasterCraft(null);
    battleAbilityID.current = newBattleAbilityID();
    setSpecialUsedThisBattle(false);
    setMatchMode(aiMatchMode);
    setScreen("battle");
  };

  const cancelQueue = () => {
    matchmaker.cancel();
    setAiDifficultyDialogSource(null);
    resetMatch();
    setXpChange(null);
    setCoinReward(null);
    setRoundMasterCraft(null);
    setScreen("menu");
  };

  const exitBattle = () => {
    resetMatch();
    setXpChange(null);
    setCoinReward(null);
    setRoundMasterCraft(null);
    if (playKind === "online") {
      closeOnlineSocket(status !== "match-result");
      setOnlineMatch(null);
      setScreen("menu");
    } else if (queueState) setScreen("queue");
    else setScreen("menu");
  };

  const playAgain = () => {
    if (playKind === "online" && onlineMatch) {
      closeOnlineSocket();
      setOnlineMatch(null);
      void startMatchmaking(matchMode);
    } else if (queueState) startAiMatch(selectedAiDifficulty);
    else void startMatchmaking(matchMode);
  };

  const runAccountAction = (action: AccountAction) => {
    if (action === "ranked") void startMatchmaking("ranked");
    else setScreen(action);
  };

  const requireAccount = (action: AccountAction) => {
    if (!accountVerified) return;
    if (account?.authenticated && !account.needsUsername) {
      runAccountAction(action);
      return;
    }
    setPendingAccountAction(action);
    setAccountDialogOpen(true);
  };

  const completeAccount = (session: AccountSession) => {
    setAccount({ ...session, cosmetics: normalizeCosmeticLoadout(session.cosmetics) });
    setAccountVerified(true);
    setXp(session.xp);
    setAccountDialogOpen(false);
    if (pendingAccountAction) runAccountAction(pendingAccountAction);
    setPendingAccountAction(null);
  };

  const handleSignOut = async (): Promise<boolean> => {
    if (account?.development) return false;
    try {
      await signOutAccount();
    } catch {
      // The current account remains active if sign-out fails.
      return false;
    }
    setProfileOpen(false);
    clearAccountPresentation();
    setXp(0);
    setCoinBalance(null);
    setSpecialItemCounts({});
    setEquippedSpecialItemId(null);
    setAccount(null);
    setAccountVerified(false);
    setScreen("menu");
    try {
      const session = await loadAccountSession();
      setAccount({ ...session, cosmetics: normalizeCosmeticLoadout(session.cosmetics) });
      setAccountVerified(true);
      setXp(session.xp);
    } catch {
      // Sign-out succeeded; local guest play remains available if session refresh fails.
    }
    return true;
  };

  const handleCosmeticsChange = useCallback((cosmetics: CosmeticLoadout) => {
    setAccount((current) => current ? { ...current, cosmetics: normalizeCosmeticLoadout(cosmetics) } : current);
  }, []);

  const handleCoinBalanceChange = useCallback((nextBalance: number) => {
    setCoinBalance(nextBalance);
  }, []);

  const handleSpecialItemsChange = useCallback((counts: SpecialItemCounts) => {
    setSpecialItemCounts({ ...counts });
  }, []);

  const handleEquipSpecialItem = useCallback(async (itemId: string) => {
    const previousItemID = equippedSpecialItemId;
    setEquippedSpecialItemId(itemId);
    try {
      const equippedItemID = await equipSpecialItem(itemId);
      setEquippedSpecialItemId(equippedItemID);
    } catch (error) {
      setEquippedSpecialItemId(previousItemID);
      throw error;
    }
  }, [equippedSpecialItemId]);

  const consumeBlueprint = useCallback(async () => {
    if (specialUsedThisBattle || equippedSpecialItemId !== CRAFTING_BLUEPRINT_ID) return false;
    try {
      const specialState = await useSpecialItem(CRAFTING_BLUEPRINT_ID, battleAbilityID.current);
      setSpecialItemCounts(specialState.specialItemCounts);
      setEquippedSpecialItemId(specialState.equippedSpecialItemId);
      setSpecialUsedThisBattle(true);
      return true;
    } catch (error) {
      if (error instanceof Error && error.message === "SPECIAL_ITEM_ALREADY_USED") setSpecialUsedThisBattle(true);
      clientWarn("special_item_use_failed", {
        itemId: CRAFTING_BLUEPRINT_ID,
        message: error instanceof Error ? error.message : "unknown"
      });
      return false;
    }
  }, [equippedSpecialItemId, specialUsedThisBattle]);

  const consumeFreeze = useCallback(async () => {
    if (specialUsedThisBattle || equippedSpecialItemId !== FREEZE_SPECIAL_ITEM_ID) return false;
    try {
      const onlineMatch = useGameStore.getState().playKind === "online";
      const specialState = await useSpecialItem(FREEZE_SPECIAL_ITEM_ID, battleAbilityID.current, onlineMatch);
      if (!onlineMatch) {
        useGameStore.getState().applyFreeze(Date.now() + SPECIAL_FREEZE_DURATION_MS);
      }
      setSpecialItemCounts(specialState.specialItemCounts);
      setEquippedSpecialItemId(specialState.equippedSpecialItemId);
      setSpecialUsedThisBattle(true);
      return true;
    } catch (error) {
      if (error instanceof Error && error.message === "SPECIAL_ITEM_ALREADY_USED") setSpecialUsedThisBattle(true);
      clientWarn("special_item_use_failed", {
        itemId: FREEZE_SPECIAL_ITEM_ID,
        message: error instanceof Error ? error.message : "unknown"
      });
      return false;
    }
  }, [equippedSpecialItemId, specialUsedThisBattle]);

  const consumeScramble = useCallback(async () => {
    if (specialUsedThisBattle || equippedSpecialItemId !== SCRAMBLE_SPECIAL_ITEM_ID) return false;
    try {
      const onlineMatch = useGameStore.getState().playKind === "online";
      const specialState = await useSpecialItem(SCRAMBLE_SPECIAL_ITEM_ID, battleAbilityID.current, onlineMatch);
      setSpecialItemCounts(specialState.specialItemCounts);
      setEquippedSpecialItemId(specialState.equippedSpecialItemId);
      setSpecialUsedThisBattle(true);
      return true;
    } catch (error) {
      if (error instanceof Error && error.message === "SPECIAL_ITEM_ALREADY_USED") setSpecialUsedThisBattle(true);
      clientWarn("special_item_use_failed", {
        itemId: SCRAMBLE_SPECIAL_ITEM_ID,
        message: error instanceof Error ? error.message : "unknown"
      });
      return false;
    }
  }, [equippedSpecialItemId, specialUsedThisBattle]);

  useEffect(() => {
    if (!accountVerified || !account?.authenticated || account.needsUsername) {
      setCoinBalance(null);
      setSpecialItemCounts({});
      setEquippedSpecialItemId(null);
      return;
    }
    let active = true;
    void loadShop().then((shop) => {
      if (active) {
        setCoinBalance(shop.coinBalance);
        setSpecialItemCounts(shop.specialItemCounts);
        setEquippedSpecialItemId(shop.equippedSpecialItemId);
      }
    }).catch(() => {
      // Keep the last known balance if the wallet cannot be refreshed.
    });
    return () => { active = false; };
  }, [account?.authenticated, account?.needsUsername, account?.playerId, accountVerified]);

  const playerCosmetics = normalizeCosmeticLoadout(account?.cosmetics);
  const playerWallpaper = cosmeticItem(playerCosmetics.wallpaperId);
  const rivalCosmetics = playKind === "online"
    ? normalizeCosmeticLoadout(onlineMatch?.opponentCosmetics)
    : { ...DEFAULT_COSMETIC_LOADOUT, craftingTableId: "obsidian-black", faceplateId: "pumpkin-pal" };

  return (
    <main className={`minecraft-screen wallpaper-${cosmeticThemeClass(playerCosmetics.wallpaperId)} ${playerWallpaper?.assetPath ? "custom-wallpaper" : ""}`} style={{ ...textureStyles, ...(playerWallpaper?.assetPath ? { "--panorama": `url(${playerWallpaper.assetPath})` } : {}) } as CSSProperties}>
      <div className="world-shade" />
      <div className="ui-scale-shell h-screen w-full origin-top-left min-[1536px]:h-[92.5926vh] min-[1536px]:w-[92.5926%] min-[1536px]:scale-[1.08] min-[1920px]:h-[86.9565vh] min-[1920px]:w-[86.9565%] min-[1920px]:scale-[1.15] min-[2560px]:h-[83.3333vh] min-[2560px]:w-[83.3333%] min-[2560px]:scale-[1.2]">
        <AnimatePresence mode="wait">
          {screen === "menu" && <MenuScreen key="menu" xp={xp} coinBalance={coinBalance} specialItemCounts={specialItemCounts} equippedSpecialItemId={equippedSpecialItemId} account={account} accountVerified={accountVerified} onlineCount={onlineCount} onQuick={() => void startMatchmaking("quick")} onRanked={() => requireAccount("ranked")} onPlayAi={() => setAiDifficultyDialogSource("menu")} onLeaderboard={() => requireAccount("leaderboard")} onShop={() => { setShopInitialCategory("all"); requireAccount("shop"); }} onSpecialItemsShop={() => { setShopInitialCategory("special"); requireAccount("shop"); }} onEquipSpecialItem={handleEquipSpecialItem} onLearn={() => setScreen("learn")} onAccount={() => setAccountDialogOpen(true)} onProfile={() => { if (accountVerified) setProfileOpen(true); }} />}
          {screen === "queue" && queueState && <QueueScreen key="queue" state={queueState} onPlayAi={() => setAiDifficultyDialogSource("queue")} onCancel={cancelQueue} onRetry={() => void startMatchmaking(queueState.mode)} />}
          {screen === "learn" && <LearnScreen key="learn" resolve={resolve} playerName={account?.username ?? "PLAYER"} playerLevel={playerLevelProgress(xp).level} playerLoadout={playerCosmetics} onBack={() => setScreen("menu")} />}
          {screen === "leaderboard" && account && <LeaderboardScreen key="leaderboard" currentUsername={account.username} onBack={() => setScreen("menu")} />}
          {screen === "shop" && <ShopScreen key="shop" initialCategory={shopInitialCategory} onLoadoutChange={handleCosmeticsChange} onCoinBalanceChange={handleCoinBalanceChange} onSpecialItemsChange={handleSpecialItemsChange} onBack={() => setScreen("menu")} />}
          {screen === "about" && <AboutScreen key="about" />}
          {screen === "privacy" && <PrivacyPolicyScreen key="privacy" />}
          {screen === "terms" && <TermsOfServiceScreen key="terms" />}
          {screen === "battle" && !revealed && <BattleScreen key="battle" resolve={resolve} onExit={exitBattle} mode={matchMode} seconds={seconds} preparing={preparing} searching={Boolean(queueState && !queueState.error && playKind === "ai")} frozen={frozen} playerName={account?.username ?? "PLAYER"} playerLevel={playerLevelProgress(xp).level} rivalName={playKind === "online" ? onlineMatch?.opponentUsername ?? "OPPONENT" : "FORGE BOT"} rivalLevel={playKind === "online" ? playerLevelProgress(onlineMatch?.opponentXp ?? 0).level : null} playerLoadout={playerCosmetics} rivalLoadout={rivalCosmetics} playerSpecialItemId={equippedSpecialItemId} rivalSpecialItemId={playKind === "online" ? onlineMatch?.opponentSpecialItemId ?? null : null} masterEligible={playKind === "online" && Boolean(account?.authenticated && !account.needsUsername)} blueprintCount={equippedSpecialItemId === CRAFTING_BLUEPRINT_ID ? specialItemCounts[CRAFTING_BLUEPRINT_ID] ?? 0 : null} freezeCount={equippedSpecialItemId === FREEZE_SPECIAL_ITEM_ID ? specialItemCounts[FREEZE_SPECIAL_ITEM_ID] ?? 0 : null} scrambleCount={equippedSpecialItemId === SCRAMBLE_SPECIAL_ITEM_ID ? specialItemCounts[SCRAMBLE_SPECIAL_ITEM_ID] ?? 0 : null} specialUsed={specialUsedThisBattle} onUseBlueprint={consumeBlueprint} onUseFreeze={consumeFreeze} onUseScramble={consumeScramble} />}
          {screen === "battle" && status === "round-result" && <RoundResultScreen key="round-result" resolve={resolve} onExit={exitBattle} mode={matchMode} playerFaceplateId={playerCosmetics.faceplateId} rivalFaceplateId={rivalCosmetics.faceplateId} nextRoundProgress={roundTransition.progress} nextRoundSeconds={roundTransition.seconds} masterCraft={roundMasterCraft} />}
          {screen === "battle" && status === "match-result" && <MatchXpScreen key="match-xp" onExit={exitBattle} onRematch={playAgain} mode={matchMode} playerFaceplateId={playerCosmetics.faceplateId} rivalFaceplateId={rivalCosmetics.faceplateId} xp={xp} xpChange={xpChange} rivalXp={rivalXp} noXpLabel={playKind === "ai" ? "PRACTICE MATCH" : !account?.authenticated ? "ACCOUNT REQUIRED" : matchMode === "ranked" ? "RANKED MATCH" : null} coinReward={coinReward} />}
        </AnimatePresence>
      </div>
      {aiDifficultyDialogSource === screen && (
        <AiDifficultyDialog
          selected={selectedAiDifficulty}
          onChoose={startAiMatch}
          onClose={() => setAiDifficultyDialogSource(null)}
        />
      )}
      {accountDialogOpen && <AccountDialog account={account} onClose={() => setAccountDialogOpen(false)} onComplete={completeAccount} />}
      {profileOpen && account?.authenticated && !account.needsUsername && <ProfileStatsModal account={account} onClose={() => setProfileOpen(false)} onSignOut={handleSignOut} />}
      {matchmaker.offer && <MatchOfferDialog offer={matchmaker.offer} onAccept={matchmaker.acceptOffer} onDecline={matchmaker.declineOffer} />}
    </main>
  );
}
